import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Trash2, Check, Pause, X as XIcon, Send, Loader2, AlertTriangle, RefreshCcw, Calendar, CalendarOff, Download, GitCompareArrows, Undo2, Redo2, MessageSquare, Pencil, Plus, ChevronDown, ChevronRight, Clock, Hourglass, PenLine } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { expandPlanToVisits } from "@/lib/planVisits";
import { treatmentPlanItemNetCentavos } from "@/lib/treatmentPlanFeeDisplay";
import { buildVisitChainWires } from "./visitChainWires";
import PhaseTabsRow, { derivePhaseNumbers } from "./PhaseTabsRow";
import PhaseTimingRow from "./PhaseTimingRow";
import LineEditControls, { buildLinePresets, TimeControl } from "@/components/doctor/LineEditControls";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle2, Circle, FlaskConical } from "lucide-react";
import CompleteVisitDialog from "./CompleteVisitDialog";
import BillingStateCard from "./BillingStateCard";
import ProposalAcceptanceChecklist from "./ProposalAcceptanceChecklist";
import DeclinedProposalView from "./DeclinedProposalView";
import AcceptedPlanSequence from "./AcceptedPlanSequence";
import ScheduledPlanItinerary from "./ScheduledPlanItinerary";
import SupersededPlanView from "./SupersededPlanView";
import { buildProcedureColorMap, procedureColor } from "./procedureColors";
import { hasUnassignedPatientFacingWork } from "@/lib/treatmentPlanPhaseGuards";

// Per-line discount applied? (used to drive strike-through on gross.)
function hasLineDiscount(item) {
  return (item.discount_pct != null && Number(item.discount_pct) > 0)
      || (item.discount_amount_centavos != null && Number(item.discount_amount_centavos) > 0);
}
const SURFACE_ORDER_TX = ["M","O","D","B","L","I"];
function splitSurfacesTx(s) {
  if (!s) return [];
  const set = new Set(String(s).toUpperCase().split("").filter((c) => SURFACE_ORDER_TX.includes(c)));
  return SURFACE_ORDER_TX.filter((c) => set.has(c));
}
function toPhaseNum(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}
function getItemTeeth(item) {
  const teethArr = Array.isArray(item?.teeth)
    ? item.teeth.map((t) => (typeof t === "object" ? t?.tooth_number : t)).filter(Boolean)
    : [];
  if (teethArr.length > 0) return teethArr.map(String);
  if (item?.tooth_number) return [String(item.tooth_number)];
  return [];
}
function toothSummary(item) {
  const teeth = getItemTeeth(item);
  if (teeth.length === 0) return "Whole-mouth";
  // Per-surface procedures (composites, etc.) show their surfaces next to the
  // tooth, e.g. "Tooth 42 · OL". Whole-tooth / multi-tooth work carries none.
  const surfaces = splitSurfacesTx(item?.surface);
  if (teeth.length === 1) {
    return surfaces.length > 0 ? `Tooth ${teeth[0]} · ${surfaces.join("")}` : `Tooth ${teeth[0]}`;
  }
  return `Teeth ${teeth.join(", ")}`;
}
function itemChairMinutes(item) {
  const visits = Array.isArray(item?.procedure_visits) ? item.procedure_visits : [];
  return visits.reduce((sum, v) => sum + (Number(v.chair_min) || 0), 0);
}
function itemStagePhaseSummary(item, phaseNames = {}) {
  const visits = Array.isArray(item?.procedure_visits) ? item.procedure_visits : [];
  const phases = new Set();
  for (const visit of visits) {
    const n = toPhaseNum(visit.phase_number);
    if (n) phases.add(n);
  }
  if (phases.size === 0) return "Needs appointment";
  return [...phases]
    .sort((a, b) => a - b)
    .map((n) => phaseNames?.[String(n)] || `Appointment ${n}`)
    .join(", ");
}
function visitPhaseOptions(currentVisit, phaseNumbers = []) {
  const currentPhase = toPhaseNum(currentVisit?.phase_number);
  const options = [...(phaseNumbers || [])];
  if (currentPhase != null && !options.includes(currentPhase)) options.push(currentPhase);
  return [...new Set(options)]
    .sort((a, b) => a - b)
    .map((n) => ({ number: n, disabled: false }));
}
function colorWithAlpha(color, alpha = "18", fallback = "rgba(148, 163, 184, 0.12)") {
  const raw = String(color || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return `${raw}${alpha}`;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const expanded = raw
      .slice(1)
      .split("")
      .map((ch) => ch + ch)
      .join("");
    return `#${expanded}${alpha}`;
  }
  return fallback;
}
function activeTreatmentItems(items = []) {
  return (items || []).filter((item) => (
    !item.is_amendment && !["Cancelled", "Declined"].includes(item.status)
  ));
}
function itemDisplayName(item) {
  return item?.display_label || item?.procedure_name || item?.name_snapshot || "Procedure";
}
function buildVisitSequenceNudges(items = []) {
  const nudges = [];
  for (const item of activeTreatmentItems(items)) {
    const visits = Array.isArray(item.procedure_visits) ? [...item.procedure_visits] : [];
    visits.sort((a, b) => Number(a.sequence) - Number(b.sequence));
    if (visits.length < 2) continue;
    const phases = visits.map((visit) => toPhaseNum(visit.phase_number ?? item.phase_number));
    if (phases.some((phase) => phase == null)) continue;
    const uniquePhases = new Set(phases);
    if (uniquePhases.size !== 1) continue;

    const firstPhase = phases[0];
    const targetPhases = visits.map((_, index) => firstPhase + index);
    nudges.push({
      item,
      visits,
      currentPhase: firstPhase,
      targetPhases,
    });
  }
  return nudges;
}
function visitSequenceChanges(nudge) {
  if (!nudge?.item || !Array.isArray(nudge?.visits)) return [];
  return nudge.visits
    .map((visit, index) => ({
      visit,
      targetPhase: nudge.targetPhases?.[index],
      currentPhase: toPhaseNum(visit?.phase_number ?? nudge.item.phase_number),
    }))
    .filter(({ visit, targetPhase, currentPhase }) => (
      visit?.id && Number.isInteger(targetPhase) && currentPhase !== targetPhase
    ));
}

// =====================================================================
// TxPlanBuilder — bottom-anchored items list + total + send button.
// Items grouped by tooth (V1 mental model).
// =====================================================================
//
// Status display:
//   Draft / Proposed     → 🗑 delete  (and inline edit fee)
//   Per-item action row  → Accept / Defer / Decline (when Proposed)
//   Accepted / Scheduled → status pill + (later) schedule action
//   Completed            → status pill, frozen
//   Declined             → strike-through row
// =====================================================================

const STATUS_BADGES = {
  Candidate:           "bg-slate-100 text-slate-600",
  Proposed:            "bg-violet-50 text-violet-700",
  Accepted:            "bg-emerald-50 text-emerald-700",
  Scheduled:           "bg-amber-50 text-amber-700",
  Completed:           "bg-teal-50 text-teal-700",
  Deferred:            "bg-slate-100 text-slate-500",
  Declined:            "bg-rose-50 text-rose-700",
};

const PLAN_STATUS_BADGES = {
  Draft:               "bg-slate-100 text-slate-700",
  Proposed:            "bg-violet-50 text-violet-700",
  "Partially Accepted":"bg-amber-50 text-amber-700",
  Accepted:            "bg-emerald-50 text-emerald-700",
  "Needs Booking":     "bg-violet-50 text-violet-700",
  Scheduled:           "bg-emerald-50 text-emerald-700",
  "In Progress":       "bg-amber-50 text-amber-800",
  Paused:              "bg-orange-50 text-orange-700",
  Completed:           "bg-teal-50 text-teal-700",
  AR:                  "bg-red-50 text-red-700",
  Expired:             "bg-rose-50 text-rose-700",
  Abandoned:           "bg-stone-100 text-stone-700",
  Rejected:            "bg-rose-50 text-rose-700",
  Superseded:          "bg-slate-100 text-slate-500",
};

function formatPHP(centavos) {
  if (centavos == null) return "—";
  return "₱" + Math.round(centavos / 100).toLocaleString("en-US");
}

function formatVisitDate(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return String(ts); }
}

// =====================================================================
// PlanStatusStrip — execution-mode dashboard strip at the top of an
// Accepted plan. Shows a progress bar (visits done / total) and a
// money breakdown (total / paid / outstanding). Hidden on Draft and
// Proposed plans where it'd be premature.
// =====================================================================
function PlanStatusStrip({ plan, planBalance }) {
  const items = Array.isArray(plan?.items) ? plan.items : [];
  const liveItems = items.filter((i) => !["Cancelled","Declined"].includes(i.status));
  const visits = liveItems.flatMap((i) => Array.isArray(i.procedure_visits) ? i.procedure_visits : []);
  const totalVisits = visits.length;
  const doneVisits  = visits.filter((v) => v.completed_at != null).length;
  const pct = totalVisits > 0 ? Math.round((doneVisits / totalVisits) * 100) : 0;

  // Pull total from balance RPC when available, fall back to plan
  // record's rolled-up total, fall back to summing live item fees so
  // the strip never reads ₱0 just because planBalance is still loading.
  const fallbackTotal = liveItems.reduce(
    (s, i) => s + Number(i.fee_centavos || 0) - Number(i.discount_amount_centavos || 0),
    0,
  );
  const totalCents       = Number(planBalance?.total_centavos ?? plan?.total_price_centavos ?? fallbackTotal);
  const paidCents        = Number(planBalance?.paid_centavos ?? 0);
  const outstandingCents = Number(planBalance?.balance_centavos ?? Math.max(0, totalCents - paidCents));

  return (
    <div
      className="px-4 sm:px-5 py-3.5 border-b border-slate-100 space-y-2.5"
      data-testid="tx-plan-status-strip"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          Progress
        </span>
        <span className="text-[11px] tabular-nums text-slate-500">
          {doneVisits} / {totalVisits} visit{totalVisits === 1 ? "" : "s"} done · {pct}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-emerald-500 transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] pt-1 tabular-nums">
        <span className="text-slate-700">
          <span className="font-semibold text-slate-900">{formatPHP(totalCents)}</span>
          <span className="text-slate-500"> total</span>
        </span>
        <span className="text-emerald-700">
          <span className="font-semibold">{formatPHP(paidCents)}</span>
          <span className="text-emerald-600/70"> paid</span>
        </span>
        <span className={outstandingCents > 0 ? "text-amber-700" : "text-slate-400"}>
          <span className="font-semibold">{formatPHP(outstandingCents)}</span>
          <span className={outstandingCents > 0 ? "text-amber-600/70" : "text-slate-400"}> outstanding</span>
        </span>
      </div>
    </div>
  );
}

function ProposalReadinessStrip({
  isDraft,
  noItems,
  phasesEnabled,
  unassignedWork,
  sequenceNudges,
  readOnlyActions,
}) {
  if (!isDraft) return null;

  const hasUnassigned = phasesEnabled && Number(unassignedWork?.count || 0) > 0;
  const hasSequenceReview = (sequenceNudges || []).length > 0;
  const ready = !noItems && !hasUnassigned && !hasSequenceReview && !readOnlyActions;
  const headline = noItems
    ? "Build treatment list"
    : hasUnassigned
      ? "Place work into appointments"
      : hasSequenceReview
        ? "Review visit sequence"
        : readOnlyActions
          ? "Demo preview"
          : "Ready to send";
  const detail = noItems
    ? "No treatments yet"
    : hasUnassigned
      ? `${unassignedWork.count} item${unassignedWork.count === 1 ? "" : "s"} need appointment placement`
      : hasSequenceReview
        ? `${sequenceNudges.length} multi-visit item${sequenceNudges.length === 1 ? "" : "s"} need a sequence check`
        : readOnlyActions
          ? "Patient-facing actions are disabled"
          : "Appointments and proposal gate are clear";

  const checks = [
    { key: "items", label: "Treatment", ok: !noItems },
    { key: "appointments", label: "Appointments", ok: !hasUnassigned },
    { key: "sequence", label: "Sequence", ok: !hasSequenceReview },
    { key: "send", label: "Send", ok: ready || readOnlyActions },
  ];

  return (
    <div
      className={`px-4 sm:px-5 py-3 border-b ${
        ready
          ? "border-emerald-100 bg-emerald-50/70"
          : hasUnassigned || hasSequenceReview
            ? "border-amber-100 bg-amber-50/70"
            : "border-slate-100 bg-slate-50"
      }`}
      data-testid="tx-plan-readiness-strip"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Proposal readiness
          </p>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm font-bold text-slate-900">{headline}</span>
            <span className="text-xs text-slate-500">{detail}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {checks.map((check) => (
            <span
              key={check.key}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold ${
                check.ok
                  ? "border-emerald-200 bg-white text-emerald-700"
                  : "border-slate-200 bg-white text-slate-500"
              }`}
              data-testid={`tx-plan-readiness-${check.key}`}
            >
              {check.ok ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
              {check.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// VisitTimelineStrip — embedded visit timeline for execution-mode
// items. One row per procedure_visit with its state (completed,
// next-pending-not-scheduled, next-pending-scheduled, future-pending)
// and the relevant action verb. Renders inside ItemRow's State zone
// when the plan is in execution mode (item status Accepted /
// Scheduled / Completed). Replaces the standalone CaseTimelineCard
// for these items so the procedure's identity and its execution state
// live on a single card per dentist's mental model.
// =====================================================================
function VisitTimelineStrip({ item, procedure, canEdit, onScheduleItem, onChanged }) {
  const visits = Array.isArray(item.procedure_visits) ? [...item.procedure_visits] : [];
  visits.sort((a, b) => Number(a.sequence) - Number(b.sequence));
  const total = visits.length;
  const itemScheduled = !!item.scheduled_appointment_id;
  const nextPending = visits.find((v) => !v.completed_at) || null;

  const [completeFor, setCompleteFor] = useState(null);
  const requiresLab = !!procedure?.requires_lab_work;

  if (visits.length === 0) return null;

  return (
    <div className="border-t border-slate-200">
      <ol className="divide-y divide-slate-100">
        {visits.map((v) => {
          const isDone     = !!v.completed_at;
          const isNext     = !isDone && v.id === nextPending?.id;
          const isFuture   = !isDone && !isNext;
          const showBook   = isNext && !itemScheduled && canEdit;
          const showComplete = isNext && itemScheduled && canEdit;

          return (
            <li
              key={v.id}
              className={`px-3 py-2 ${isNext ? "bg-violet-50/30" : ""}`}
              data-testid={`tx-item-${item.id}-visit-${v.id}`}
            >
              <div className="flex items-start gap-2.5">
                <div className="pt-0.5 shrink-0">
                  {isDone ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : isNext ? (
                    <Clock className="w-4 h-4 text-violet-600" />
                  ) : (
                    <Circle className="w-4 h-4 text-slate-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-slate-900">
                    <span className="font-semibold">Visit {v.sequence} of {total}</span>
                    {v.template_label && total > 1 ? (
                      <span className="text-slate-500"> · {v.template_label}</span>
                    ) : null}
                    {v.chair_min ? (
                      <span className="text-[11px] text-slate-500 tabular-nums ml-1.5">
                        · {v.chair_min} min
                      </span>
                    ) : null}
                  </div>

                  {isDone ? (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      ✓ {formatVisitDate(v.completed_at)}
                      {Array.isArray(v.chip_log) && v.chip_log.length > 0 ? (
                        <span> · {v.chip_log.length} chip{v.chip_log.length === 1 ? "" : "s"}</span>
                      ) : null}
                      {Array.isArray(v.materials_logged) && v.materials_logged.length > 0 ? (
                        <span> · {v.materials_logged.length} material{v.materials_logged.length === 1 ? "" : "s"}</span>
                      ) : null}
                    </div>
                  ) : isNext ? (
                    <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                      {!itemScheduled ? (
                        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                          <Circle className="w-3 h-3" /> Not scheduled yet
                        </span>
                      ) : (
                        <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Scheduled
                        </span>
                      )}
                      {showBook ? (
                        <button
                          type="button"
                          onClick={() => onScheduleItem?.(item)}
                          data-testid={`tx-item-${item.id}-book-visit`}
                          className="px-2.5 py-1 text-[11px] font-semibold rounded-md border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 inline-flex items-center gap-1"
                        >
                          <Calendar className="w-3 h-3" /> Book this visit
                        </button>
                      ) : null}
                      {showComplete ? (
                        <button
                          type="button"
                          onClick={() => setCompleteFor({ visit: v, item, procedure })}
                          data-testid={`tx-item-${item.id}-complete-visit`}
                          className="px-2.5 py-1 text-[11px] font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Mark complete
                        </button>
                      ) : null}
                      {requiresLab ? (
                        <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                          <FlaskConical className="w-3 h-3" /> requires lab
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-400 mt-0.5">Pending</div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {completeFor ? (
        <CompleteVisitDialog
          open={!!completeFor}
          onOpenChange={(o) => { if (!o) setCompleteFor(null); }}
          visit={completeFor.visit}
          item={completeFor.item}
          procedure={completeFor.procedure}
          onCompleted={() => { setCompleteFor(null); onChanged?.(); }}
        />
      ) : null}
    </div>
  );
}

export default function TxPlanBuilder({
  plan,                          // { id, status, items, ... } | null
  busy = {},                     // { propose, item: {[id]: bool} }
  canPropose = true,
  canAccept = true,
  canEdit = true,
  onDeleteItem,                  // (item_id) => void
  onSetItemStatus,               // (item_id, 'Accepted'|'Deferred'|'Declined', reason?) => void
  onSetItemDiscount,             // (item_id, {pct, amount, reason}) => Promise — any plan status
  onSetItemPrice,                // (item_id, centavos) => Promise — Draft only
  onSetItemNote,                 // (item_id, notes|null) => Promise — Draft only
  patient = null,                // patient object — for senior_pwd_tag preset highlight
  procedures = [],               // catalog — for additional_surface_pct lookup
  chairSetupMinutes = 15,        // clinic-wide setup overhead, added once per appointment/phase
  onMarkAccepted,                // () => void  — opens the lifecycle v2 MarkAcceptedDialog
  onClosePlan,                   // () => void  — opens the ClosePlanDialog (Phase 5 final close)
  onCollectPayment,              // () => void  — opens the CollectPaymentDialog (Phase 5 mid/AR)
  onBuildMultiTooth,             // () => void  — opens BuildMultiToothDialog (Phase 2 chart UX)
  planBalance = null,            // {balance_centavos, total_centavos, ...} | null
  onPropose,                     // (plan_id) => void
  onResendEmail,                 // (plan_id) => void  — Proposed plans only
  onRevise,                      // (plan_id) => void
  onOpenPlan,                    // (plan_id) => void  — switch the active plan (superseded → successor)
  onDeleteDraftPlan,             // (plan_id) => void
  onScheduleItem,                // (item) => void  — open the schedule picker
  onUnscheduleItem,              // (item_id) => void
  onSetItemPhase,                // (item_id, phase_number|null) => void
  onSetToothPhase,               // (tooth_number, phase_number|null) => void
  onRenamePhase,                 // (phase_number, name) => void
  onDeletePhase,                 // (phase_number) => void
  onAddPhase,                    // (next_number) => void
  onSchedulePhase,               // (phase_number) => void  (open picker for the whole phase)
  onSetPhaseTiming,              // (phase_number, offset_days|null, after_phase|null) => Promise
  onSetVisitPhase,               // (item_id, visit_id, phase_number|null) => void
  onSetToothNote,                // (tooth_number, text|null) => Promise   — used to CLEAR (text=null)
  onOpenNote,                    // (tooth_number) => void                  — opens the popup modal
  onDownloadPdf,                 // (plan_id) => void
  onSignDocument,                // (plan) => void
  signatureDocument = null,      // latest signed_documents payload for this plan
  signatureBusy = false,
  onReload,                      // () => void  — re-fetches the plan (used after amendments save)
  patientName,                   // string — passed to BillingStateCard's amendment dialog header
  onComparePlans,                // (plan_id) => void  — open the picker
  canCompare = false,            // patient has 2+ plans
  readOnlyActions = false,        // tutorial/demo mode: render real actions but disable patient-facing effects
  undoCount = 0,
  redoCount = 0,
  undoBusy = false,
  onUndo,
  onRedo,
  onVisitStagesChanged = null,   // () => void — refresh after visit-stage CRUD
}) {
  const [declineFor, setDeclineFor] = useState(null); // item_id
  const [declineReason, setDeclineReason] = useState("");
  const [activePhase, setActivePhase] = useState("all"); // 'all' | 'unassigned' | number
  const [sequenceBusyItemId, setSequenceBusyItemId] = useState(null);
  const [showBoard, setShowBoard] = useState(false); // Accepted/Partial: "Schedule visits" reveals the board
  // Undo a folded walk-in line (a line pushed onto the plan at checkout via
  // "Add today's work to the plan"). Reversible before the plan is paid; the
  // server (unfold_plan_line) posts a reversal + cancels the item, and hard-
  // blocks once any payment allocates to the fold charge.
  const [unfoldTarget, setUnfoldTarget] = useState(null); // the folded amendment item, or null
  const [unfolding, setUnfolding] = useState(false);

  const performUnfold = useCallback(async (item) => {
    if (!item?.id || !plan?.id) return;
    setUnfolding(true);
    const { error } = await supabase.rpc("unfold_plan_line", {
      p_plan_id: plan.id,
      p_line_item_id: item.id,
    });
    setUnfolding(false);
    if (error) {
      // Server enforces the "locked after payment" guard and returns a plain
      // sentence ("This folded work has already been paid ..."). Surface it.
      toast.error(error.message ?? "Couldn't remove this from the plan.");
      return;
    }
    toast.success("Removed from the plan.");
    setUnfoldTarget(null);
    onReload?.();
  }, [plan?.id, onReload]);

  const phaseNumbers = useMemo(
    () => {
      const raw = derivePhaseNumbers(plan?.items ?? [], plan?.phase_names ?? {});
      // A new draft opens with at least 2 appointment lanes so the dentist
      // has somewhere to organize the visits into; the visits themselves
      // still start in "Needs appointment" (unassigned). Non-draft plans
      // reflect only their real phases.
      if (canEdit && plan?.status === "Draft") {
        const floor = Math.max(2, raw.length ? Math.max(...raw) : 0);
        return Array.from({ length: floor }, (_, i) => i + 1);
      }
      return raw;
    },
    [plan?.items, plan?.phase_names, plan?.status, canEdit],
  );

  // Auto-fall-back to "All" if the active phase no longer has any
  // items AND no name (e.g. user renamed-then-deleted).
  useEffect(() => {
    if (activePhase === "all") return;
    if (activePhase === "unassigned") return;
    if (!phaseNumbers.includes(activePhase)) setActivePhase("all");
  }, [activePhase, phaseNumbers]);

  // Visit-aware phase filtering. After 20260614000001, every item has
  // ≥1 procedure_visits. An item appears in every phase tab where it
  // has a visit (a multi-visit bridge whose stages span Appt 2/3/4
  // shows up in all three). Falls back to item.phase_number for
  // legacy items without visits, defensive only.
  // Originals only — amendments render in their own dedicated block
  // below (visually distinct: badge + tint, ordered chronologically).
  // Tooth grouping doesn't apply to amendments since many of them
  // (extended chair time, additional materials) aren't tooth-bound.
  const visibleItems = useMemo(() => {
    const all = (plan?.items ?? []).filter((i) => !i.is_amendment);
    if (activePhase === "all") return all;
    if (activePhase === "unassigned") {
      return all.filter((i) => {
        const visits = Array.isArray(i.procedure_visits) ? i.procedure_visits : [];
        if (visits.length === 0) return i.phase_number == null;
        return visits.some((v) => (v.phase_number ?? i.phase_number) == null);
      });
    }
    return all.filter((i) => {
      const visits = Array.isArray(i.procedure_visits) ? i.procedure_visits : [];
      if (visits.length === 0) return Number(i.phase_number) === activePhase;
      return visits.some((v) => Number(v.phase_number ?? i.phase_number) === activePhase);
    });
  }, [plan?.items, activePhase]);

  // Amendments — kept separate from originals. Sorted chronologically
  // (oldest first) so the receipt and screen tell a clean story:
  // "first the original plan, then this happened, then this happened."
  const amendmentItems = useMemo(() => {
    const all = (plan?.items ?? []).filter((i) => i.is_amendment);
    return [...all].sort((a, b) => {
      const ta = a.amended_at ? new Date(a.amended_at).getTime() : 0;
      const tb = b.amended_at ? new Date(b.amended_at).getTime() : 0;
      return ta - tb;
    });
  }, [plan?.items]);

  // Group items by tooth, then merge in any teeth that have a note but
  // no procedure code yet — otherwise notes added before a procedure
  // (the natural "I want to remember this" workflow) silently disappear
  // from the draft. Only do this on the "All" tab — notes aren't
  // phase-scoped, so it'd be misleading to show them under a specific
  // phase tab the user is filtering by.
  const grouped = useMemo(() => {
    const base = groupPlanItems(visibleItems, procedures);
    if (activePhase !== "all") return base;
    const notes = plan?.tooth_notes ?? {};
    const haveTeeth = new Set(base.map((g) => String(g.tooth ?? "")));
    const extras = Object.keys(notes)
      .filter((tooth) => tooth && notes[tooth] && !haveTeeth.has(String(tooth)))
      .map((tooth) => ({
        key: `tooth-note:${tooth}`,
        kind: "tooth",
        label: `Tooth ${tooth}`,
        tooth,
        items: [],
        subtotal: 0,
      }));
    if (extras.length === 0) return base;
    return [...base, ...extras].sort((a, b) => {
      const ta = a.tooth, tb = b.tooth;
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return Number(ta) - Number(tb);
    });
  }, [visibleItems, procedures, plan?.tooth_notes, activePhase]);
  const totals  = useMemo(() => computeTotals(plan?.items ?? []), [plan?.items]);

  // Per-phase subtotal (centavos, non-declined) for the tab labels.
  // Anchor-fee semantics: a multi-visit bridge spanning Appt 2/3/4
  // contributes its full fee to Appt 4 (cementation phase) only — Appts
  // 2 and 3 show ₱0 from that item. Visit-level phase_number is the
  // authoritative source after the universal-visits migration.
  const phaseTotals = useMemo(() => {
    const out = {};
    const visits = expandPlanToVisits(plan);
    for (const v of visits) {
      const n = Number(v.phase_number);
      if (!Number.isFinite(n) || n < 1) continue;
      out[n] = (out[n] ?? 0) + Number(v.fee_centavos || 0);
    }
    return out;
  }, [plan]);

  const procedureById = useMemo(() => {
    const map = new Map();
    for (const proc of procedures || []) {
      if (proc?.id) map.set(proc.id, proc);
    }
    return map;
  }, [procedures]);
  const procedureColorMap = useMemo(
    () => buildProcedureColorMap(procedures || []),
    [procedures],
  );

  // Per-phase chair time. procedure_visits.chair_min is raw clinical work time;
  // chair setup is appointment-level overhead, so add it once per phase when
  // at least one visit in that phase uses setup.
  const phaseChair = useMemo(() => {
    const byPhase = {};
    const visits = expandPlanToVisits(plan);
    for (const v of visits) {
      const n = Number(v.phase_number);
      if (!Number.isFinite(n) || n < 1) continue;
      if (!byPhase[n]) byPhase[n] = { raw: 0, usesSetup: false };
      byPhase[n].raw += Number(v.chair_min || 0);

      const proc = procedureById.get(v.item?.procedure_id);
      byPhase[n].usesSetup = byPhase[n].usesSetup || proc?.uses_chair_setup !== false;
    }
    const out = {};
    for (const [phase, meta] of Object.entries(byPhase)) {
      const setup = meta.usesSetup ? Number(chairSetupMinutes || 0) : 0;
      out[phase] = meta.raw + setup;
    }
    return out;
  }, [chairSetupMinutes, plan, procedureById]);

  const phaseCalendar = useMemo(() => {
    const out = {};
    const byPhase = {};
    for (const item of plan?.items ?? []) {
      const visits = Array.isArray(item?.procedure_visits) ? item.procedure_visits : [];
      for (const visit of visits) {
        const n = Number(visit?.phase_number ?? item?.phase_number);
        if (!Number.isFinite(n) || n < 1) continue;
        const appointmentId = visit?.appointment_id || item?.scheduled_appointment_id || null;
        if (!appointmentId) continue;
        if (!byPhase[n]) byPhase[n] = { ids: new Set(), durationMin: null };
        byPhase[n].ids.add(appointmentId);
        const explicitDuration = Number(visit?.appointment_duration_min);
        if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
          byPhase[n].durationMin = Math.round(explicitDuration);
          continue;
        }
        const start = visit?.appointment_scheduled_start;
        const end = visit?.appointment_scheduled_end;
        if (start && end) {
          const diff = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
          if (Number.isFinite(diff) && diff > 0) byPhase[n].durationMin = diff;
        }
      }
    }
    for (const [phase, meta] of Object.entries(byPhase)) {
      const ids = [...meta.ids];
      out[phase] = {
        appointment_id: ids.length === 1 ? ids[0] : null,
        appointment_count: ids.length,
        duration_min: meta.durationMin,
      };
    }
    // Fold in PLANNED per-appointment durations (treatment_plans.phase_meta)
    // for phases that aren't booked yet, so a Draft shows its planned minutes.
    // A booked appointment always wins over the planned value.
    const planned = plan?.phase_meta ?? {};
    for (const [phase, meta] of Object.entries(planned)) {
      const d = Number(meta?.appointment_duration_min);
      if (!Number.isFinite(d) || d <= 0) continue;
      if (out[phase]) continue;
      out[phase] = { appointment_id: null, appointment_count: 0, duration_min: Math.round(d) };
    }
    return out;
  }, [plan?.items, plan?.phase_meta]);

  const handleSetPhaseAppointmentDuration = useCallback(async (phaseNumber, durationMin) => {
    if (readOnlyActions) return;
    if (!plan?.id) return;
    const cleanDuration = Math.round(Number(durationMin));
    if (!Number.isFinite(cleanDuration) || cleanDuration < 5 || cleanDuration > 720) {
      toast.error("Appointment time must be between 5 and 720 minutes.");
      throw new Error("Invalid appointment duration.");
    }
    // Snap to the nearest 5 minutes (calendar granularity).
    const snapped = Math.max(5, Math.round(cleanDuration / 5) * 5);
    // If the appointment is already booked, edit the real calendar block.
    // If not (a Draft), just save the planned duration — no calendar needed.
    const booked = (phaseCalendar[phaseNumber]?.appointment_count || 0) > 0;
    const rpcName = booked ? "set_phase_appointment_duration" : "set_plan_phase_duration";
    const { error } = await supabase.rpc(rpcName, {
      p_plan_id: plan.id,
      p_phase_number: phaseNumber,
      p_duration_minutes: snapped,
    });
    if (error) {
      toast.error(error.message ?? "Could not update appointment time.");
      throw error;
    }
    toast.success(booked ? "Appointment time updated." : "Planned duration saved.");
    await onReload?.();
  }, [onReload, plan?.id, readOnlyActions, phaseCalendar]);

  const unassignedWork = useMemo(() => {
    let count = 0;
    let chair = 0;
    const liveItems = (plan?.items ?? []).filter((item) => !item.is_amendment && !["Cancelled", "Declined"].includes(item.status));
    for (const item of liveItems) {
      const visits = Array.isArray(item.procedure_visits) ? item.procedure_visits : [];
      if (visits.length === 0) {
        if (item.phase_number == null) {
          count += 1;
          chair += itemChairMinutes(item);
        }
        continue;
      }
      for (const visit of visits) {
        if ((visit.phase_number ?? item.phase_number) == null) {
          count += 1;
          chair += Number(visit.chair_min || 0);
        }
      }
    }
    return { count, chair };
  }, [plan?.items]);

  if (!plan) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6 text-center" data-testid="tx-plan-builder-empty">
        <p className="text-sm text-slate-500">No active plan. Tap <span className="font-semibold">+ New Plan</span> to start one.</p>
      </div>
    );
  }

  const status = plan.status;
  const displayStatus = status;
  const isDraft     = status === "Draft";
  const isProposed  = status === "Proposed" || status === "Partially Accepted";
  const liveStatuses = new Set(["Accepted", "Needs Booking", "Scheduled", "In Progress", "AR"]);
  const isTerminal  = liveStatuses.has(status) || status === "Rejected" || status === "Superseded" || status === "Completed";
  // Lifecycle noun: clinically a plan isn't a "Treatment Plan" until the
  // patient has accepted it — before that it's still a Proposal. The
  // granular status badge next to this noun keeps the exact state
  // (Draft / Proposed / etc.); this only fixes the confusing name.
  const isAcceptedLifecycle = liveStatuses.has(status) || status === "Completed";
  const planNoun    = isAcceptedLifecycle ? "Treatment Plan" : "Proposal";
  // Just-decided plans show the green accepted sequence (with a declined
  // strip for partials) instead of the board; scheduling is behind the
  // "Schedule visits" action (showBoard).
  const isAcceptedView = status === "Accepted" || status === "Partially Accepted";
  // Booked-and-working: show the read-first itinerary (roadmap of
  // appointments) instead of the editable planning board. "Edit schedule"
  // reveals the board via showBoard, same escape hatch as Accepted.
  const isScheduledView = status === "Scheduled" || status === "In Progress";
  const canCollectForStatus = liveStatuses.has(status) || status === "Completed";
  const canCloseForStatus = liveStatuses.has(status);
  const noItems     = (plan.items ?? []).length === 0;
  const isSignedDocument = signatureDocument?.status === "signed" && !!signatureDocument?.signature;
  const canSignStatus = status === "Proposed" || liveStatuses.has(status) || status === "Completed";
  const canSignDocument = !readOnlyActions && !noItems && canSignStatus && !isSignedDocument && !!onSignDocument;

  // If phases are in use on this plan, every item must be assigned to
  // one before the dentist can propose. Otherwise the email / PDF would
  // ship out a phased plan with stray "Unassigned" rows, which patients
  // find confusing.
  const phasesEnabled        = phaseNumbers.length > 0;
  const unassignedCount      = (plan.items ?? []).filter((i) => hasUnassignedPatientFacingWork(i)).length;
  // Appointment lanes are now opened by default, so "everything still in
  // Needs appointment" is the normal starting state — not a half-finished
  // plan — and must not block Send. Only nudge when the dentist has
  // started assigning visits to appointments but left some orphaned.
  const anyVisitAssigned = (plan.items ?? []).some((i) =>
    Number(i.phase_number) >= 1
    || (Array.isArray(i.procedure_visits) ? i.procedure_visits : []).some((v) => Number(v.phase_number) >= 1));
  const blockProposeOnPhases = anyVisitAssigned && unassignedCount > 0;
  const sequenceNudges       = buildVisitSequenceNudges(plan.items ?? []);

  async function applySuggestedVisitSequence(nudge) {
    if (!isDraft || !canEdit || !nudge?.item?.id) return;
    const changes = visitSequenceChanges(nudge);
    if (changes.length !== 1) {
      toast.error("Use the appointment dropdowns for this sequence until a bulk scheduling RPC is available.");
      return;
    }
    setSequenceBusyItemId(nudge.item.id);
    try {
      const usedExternalVisitHandler = !!onSetVisitPhase;
      for (const { visit, targetPhase } of changes) {
        if (onSetVisitPhase) {
          await onSetVisitPhase(nudge.item.id, visit.id, targetPhase);
        } else {
          const { error } = await supabase.rpc("set_procedure_visit_stage", {
            p_visit_id: visit.id,
            p_phase_number: targetPhase,
          });
          if (error) throw error;
        }
      }
      toast.success(`${itemDisplayName(nudge.item)} split into appointments.`);
      if (!usedExternalVisitHandler) onVisitStagesChanged?.();
    } catch (error) {
      toast.error(error?.message ?? "Could not split visits.");
    } finally {
      setSequenceBusyItemId(null);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm" data-testid="tx-plan-builder" data-tutorial-id="doctor-tx-plan-builder">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 p-4 sm:p-5 border-b border-slate-100">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid="plan-lifecycle-noun" className="text-[11px] font-bold tracking-wider uppercase text-slate-400">
              {planNoun}
            </span>
            <span className={`text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${PLAN_STATUS_BADGES[displayStatus] || PLAN_STATUS_BADGES.Draft}`}
                  data-testid={`plan-status-${plan.id}`}>
              {displayStatus}
            </span>
            <span className="text-xs text-slate-500">
              {(plan.items ?? []).length} item{(plan.items ?? []).length === 1 ? "" : "s"}
            </span>
          </div>
          {plan.notes ? (
            <p className="text-xs text-slate-500 mt-1 max-w-xl">{plan.notes}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Acceptance now happens inline in the ProposalAcceptanceChecklist
              (rendered below for Proposed plans), which owns the "Confirm
              decision" action — so the header no longer carries a separate
              "Mark Accepted" button. */}
          {isProposed && !noItems ? (
            <button
              type="button"
              onClick={() => onResendEmail?.(plan.id)}
              disabled={busy.resend}
              data-testid="tx-plan-resend-email"
              title="Resend the plan email + PDF to the patient (does not change plan status)"
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy.resend ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Resend email
            </button>
          ) : null}

          {isTerminal && liveStatuses.has(status) ? (
            <button
              type="button"
              onClick={() => onRevise?.(plan.id)}
              data-testid="tx-plan-revise"
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Revise plan
            </button>
          ) : null}

          {/* Phase 5 — Collect intermediate payment (Accepted) or AR (Completed). */}
          {canCollectForStatus
            && onCollectPayment
            && Number(planBalance?.balance_centavos) > 0 ? (
            <button
              type="button"
              onClick={() => onCollectPayment()}
              data-testid="tx-plan-collect-payment"
              title="Collect a payment against this plan"
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1"
            >
              <Check className="w-3.5 h-3.5" />
              {status === "AR" || status === "Completed" ? "Collect (AR)" : "Collect"}
              {planBalance ? <span className="font-normal opacity-75 ml-1 tabular-nums">
                ₱{Math.round(Number(planBalance.balance_centavos) / 100).toLocaleString()}
              </span> : null}
            </button>
          ) : null}

          {/* Phase 5 — Close plan (Accepted only; gate enforced server-side). */}
          {canCloseForStatus
            && onClosePlan
            && (plan.items ?? []).length > 0 ? (
            <button
              type="button"
              onClick={() => onClosePlan()}
              data-testid="tx-plan-close"
              title="Close the plan: Completed if every item is done, AR if a balance remains, or Abandoned if work is left unfinished"
              className="px-3 py-2 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1"
            >
              <Check className="w-3.5 h-3.5" /> Close plan
            </button>
          ) : null}

          {/* Phase 2 — Multi-Tooth Tool entry point in the workspace draft.
              The palette has its own (chart-driven) trigger; this one
              opens the dialog directly without a chart pre-selection. */}
          {isDraft && canEdit && onBuildMultiTooth ? (
            <button
              type="button"
              onClick={() => onBuildMultiTooth()}
              data-testid="tx-plan-build-multi-tooth"
              title="Define a Bridge / Partial Denture by picking teeth in the dialog"
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1"
            >
              <Check className="w-3.5 h-3.5" /> Multi-Tooth
            </button>
          ) : null}

          {isDraft && canEdit ? (
            <button
              type="button"
              onClick={() => onDeleteDraftPlan?.(plan.id)}
              data-testid="tx-plan-delete-draft"
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 bg-white hover:border-rose-400 hover:text-rose-600 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-1"
            >
              <Trash2 className="w-3.5 h-3.5" /> Discard draft
            </button>
          ) : null}
        </div>
      </div>

      {/* Plan status strip — only for execution-mode plans (Accepted /
          Partially Accepted / Completed). Shows progress bar + money
          breakdown so the dentist sees where the case stands at a
          glance without bouncing to the timeline below. */}
      {(status === "Accepted" || status === "Partially Accepted" || status === "Completed") ? (
        <PlanStatusStrip plan={plan} planBalance={planBalance} />
      ) : null}

      <ProposalReadinessStrip
        isDraft={isDraft}
        noItems={noItems}
        phasesEnabled={phasesEnabled}
        unassignedWork={unassignedWork}
        sequenceNudges={sequenceNudges}
        readOnlyActions={readOnlyActions}
      />

      {/* Lifecycle v2 billing state — read-only snapshot of the plan's
          billing model + paid/remaining math, sourced from
          dos_v2.get_plan_billing_state. Visible the moment the plan
          becomes Accepted; PR 3d will turn this into the front-desk's
          per-visit override surface when start_visit_checkout fires
          from checkout. */}
      {(status === "Accepted" || status === "Completed") ? (
        <div className="px-4 sm:px-5 pt-3">
          <BillingStateCard
            planId={plan.id}
            planCode={plan.plan_code}
            patientName={patientName}
            onChanged={onReload}
          />
        </div>
      ) : null}

      {/* Phase tabs + timing annotation row */}
      {(canEdit || phaseNumbers.length > 0) ? (
        <div className="px-4 sm:px-5 pt-3 pb-2 border-b border-slate-100 space-y-2" data-tutorial-id="doctor-tx-plan-phases">
          <PhaseTabsRow
            phaseNumbers={phaseNumbers}
            phaseNames={plan?.phase_names ?? {}}
            phaseTotals={phaseTotals}
            phaseChair={phaseChair}
            phaseCalendar={phaseCalendar}
            unassignedCount={unassignedWork.count}
            unassignedChair={unassignedWork.chair}
            activePhase={activePhase}
            onChange={setActivePhase}
            onRenamePhase={(n, name) => onRenamePhase?.(n, name)}
            onSetPhaseAppointmentDuration={handleSetPhaseAppointmentDuration}
            onDeletePhase={(n) => onDeletePhase?.(n)}
            onSchedulePhase={(n) => onSchedulePhase?.(n)}
            onAddPhase={(next) => {
              onAddPhase?.(next);
              setActivePhase(phaseNumbers.length === 0 ? "all" : next);
            }}
            // Appointment structure is only editable while the plan is a
            // Draft. Once Proposed (sent to the patient) the appointments are
            // read-only — no add / rename / delete / retiming.
            disabled={!canEdit || !isDraft}
          />
          {typeof activePhase === "number" ? (
            <PhaseTimingRow
              activePhase={activePhase}
              phaseNumbers={phaseNumbers}
              phaseNames={plan?.phase_names ?? {}}
              phaseMeta={plan?.phase_meta ?? {}}
              onSetTiming={(n, offsetDays, afterPhase) => onSetPhaseTiming?.(n, offsetDays, afterPhase)}
              disabled={!canEdit}
            />
          ) : null}
        </div>
      ) : null}

      {/* Banner: phases enabled but some items aren't assigned */}
      {isDraft && blockProposeOnPhases ? (
        <div
          data-testid="tx-plan-unassigned-warning"
          className="mx-4 sm:mx-5 mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            <strong>{unassignedCount}</strong> item{unassignedCount === 1 ? " needs" : "s need"} an appointment.
            Place every item before proposing so the patient sees a clean visit plan.
          </span>
        </div>
      ) : null}

      {/* Items grouped by tooth */}
      <div className="p-4 sm:p-5 space-y-4" data-testid="tx-plan-items" data-tutorial-id="doctor-tx-plan-items">
        {status === "Rejected" ? (
          <DeclinedProposalView
            plan={plan}
            onRevise={onRevise}
            onDownloadPdf={onDownloadPdf}
          />
        ) : status === "Superseded" ? (
          <SupersededPlanView
            plan={plan}
            onOpenSuccessor={(id) => onOpenPlan?.(id)}
            onDownloadPdf={onDownloadPdf}
          />
        ) : isAcceptedView && !showBoard ? (
          <AcceptedPlanSequence
            plan={plan}
            onSchedule={() => setShowBoard(true)}
          />
        ) : isScheduledView && !showBoard ? (
          <ScheduledPlanItinerary
            plan={plan}
            phaseNames={plan?.phase_names ?? {}}
            canEdit={canEdit}
            onEditSchedule={() => setShowBoard(true)}
          />
        ) : grouped.length === 0 ? (
          <div className="text-center py-8" data-testid="tx-plan-no-items">
            <p className="text-sm text-slate-500">
              {activePhase === "unassigned"
                ? "Everything is placed in an appointment."
                : isDraft
                ? "No items yet. Pick a procedure on the right and tap a tooth to add."
                : "This plan has no items."}
            </p>
          </div>
        ) : status === "Proposed" ? (
          <ProposalAcceptanceChecklist
            plan={plan}
            onConfirm={(acceptedIds) => onMarkAccepted?.(acceptedIds)}
          />
        ) : (activePhase === "all" && phasesEnabled) || (isAcceptedView && showBoard) || (isScheduledView && showBoard) ? (
          <>
          <TxAppointmentBoard
            items={plan.items ?? []}
            procedures={procedures}
            procedureColorMap={procedureColorMap}
            phaseNumbers={phaseNumbers}
            phaseNames={plan?.phase_names ?? {}}
            phaseChair={phaseChair}
            phaseCalendar={phaseCalendar}
            busy={busy}
            canEdit={canEdit}
            isDraft={isDraft}
            sequenceNudges={sequenceNudges}
            sequenceBusyItemId={sequenceBusyItemId}
            onApplyVisitSequence={applySuggestedVisitSequence}
            onSetItemPhase={onSetItemPhase}
            onSetItemPrice={onSetItemPrice}
            onDeleteItem={onDeleteItem}
            onSetVisitPhase={onSetVisitPhase}
            onVisitStagesChanged={onVisitStagesChanged}
            onSelectUnassigned={() => setActivePhase("unassigned")}
          />
          <PlanItemPriceList
            items={plan.items ?? []}
            phaseNames={plan?.phase_names ?? {}}
            canEdit={canEdit}
            isDraft={isDraft}
            onSetItemPrice={onSetItemPrice}
            onSetItemDiscount={onSetItemDiscount}
          />
          </>
        ) : (
          grouped.map(({ key: groupKey, kind, label, tooth, items, subtotal }) => {
            // Group-level phase: same on every item ⇒ that phase;
            // items split across phases ⇒ "mixed". Coerce defensively
            // — the SQL int can come through serialization as a string
            // in some JSON paths, and `typeof "1" === "number"` is false.
            const phasesOnGroup = new Set(items.map((i) => toPhaseNum(i.phase_number)));
            const groupPhase = phasesOnGroup.size === 1 ? [...phasesOnGroup][0] : "__mixed";
            const canAssignGroup = kind === "tooth" || kind === "whole_mouth" || kind === "missing_tooth";
            // Hide the group phase picker when no phases exist yet —
            // "Unassigned" is meaningless if there's nothing to assign TO.
            // The "+ Add phase" button at the top of the builder is the
            // entry point for creating the first phase.
            const showGroupPhasePicker = activePhase === "all" && canEdit && canAssignGroup && items.length > 0 && phaseNumbers.length > 0;
            // Green border when every item on this tooth is assigned to
            // a single phase (i.e. it's part of an appointment). Mixed
            // surfaces stay neutral with the amber pill below; fully
            // unassigned stays neutral too.
            const isAssignedPhase = typeof groupPhase === "number";
            const setGroupPhase = (phase) => {
              if (kind === "tooth" && tooth) {
                onSetToothPhase?.(tooth, phase);
                return;
              }
              for (const item of items) {
                onSetItemPhase?.(item.id, phase);
              }
            };
            return (
            <div
              key={groupKey}
              className={`border rounded-xl transition-colors ${
                isAssignedPhase
                  ? "border-emerald-400 bg-emerald-50"
                  : kind === "missing_tooth"
                    ? "border-amber-200 bg-amber-50/40"
                  : "border-slate-100"
              }`}
            >
              <div
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-t-xl flex-wrap ${
                  isAssignedPhase ? "bg-emerald-100" : "bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span className={`text-sm font-semibold ${kind === "missing_tooth" ? "text-amber-800" : "text-slate-700"}`}>
                    🦷 {label}
                  </span>
                  <span className="text-xs font-normal text-slate-500">
                    {items.length === 0 ? "note only" : `${items.length} item${items.length === 1 ? "" : "s"}`}
                  </span>
                  {showGroupPhasePicker ? (
                    <select
                      value={groupPhase === "__mixed" ? "__mixed" : (groupPhase == null ? "" : String(groupPhase))}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__mixed") return;        // can't pick "mixed" — display only
                        if (v === "__new") {
                          const next = phaseNumbers.length === 0 ? 1 : Math.max(...phaseNumbers) + 1;
                          setGroupPhase(next);
                        } else {
                          setGroupPhase(v === "" ? null : Number(v));
                        }
                      }}
                      data-testid={`tx-group-${groupKey}-phase`}
                      title={kind === "tooth" ? "Assign every surface on this tooth to an appointment" : "Assign this whole-mouth procedure to an appointment"}
                      className="text-[11px] font-semibold rounded border border-slate-200 bg-white px-2 py-1 text-slate-700 hover:border-slate-400"
                    >
                      <option value="">Needs appointment</option>
                      {phaseNumbers.map((n) => (
                        <option key={n} value={n}>
                          {plan?.phase_names?.[String(n)] || `Appointment ${n}`}
                        </option>
                      ))}
                      {groupPhase === "__mixed" ? (
                        <option value="__mixed" disabled>Mixed appointments…</option>
                      ) : null}
                      <option value="__new">+ New appointment</option>
                    </select>
                  ) : null}
                  {groupPhase === "__mixed" ? (
                    <span
                      data-testid={`tx-group-${groupKey}-mixed-phase`}
                      title={kind === "tooth" ? "Surfaces on this tooth are assigned to different appointments" : "Items in this group are assigned to different appointments"}
                      className="inline-flex items-center text-[10px] font-bold tracking-wider uppercase rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200"
                    >
                      Mixed
                    </span>
                  ) : null}
                </div>
                {items.length > 0 ? (
                  <div className="text-sm font-semibold text-teal-700 tabular-nums">{formatPHP(subtotal)}</div>
                ) : null}
              </div>

              {/* Per-tooth note: italic gray under the header. ✏️ opens the
                  popup modal (anchored over the chart); X clears. Empty +
                  canEdit → ghost "Add note" button that opens the modal. */}
              {tooth ? (
                plan?.tooth_notes?.[String(tooth)] ? (
                  <div className="px-3 py-2 border-b border-slate-100 flex items-start gap-2 text-xs">
                    <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                    <span className="italic text-slate-600 flex-1 whitespace-pre-wrap">
                      {plan.tooth_notes[String(tooth)]}
                    </span>
                    {canEdit ? (
                      <>
                        <button
                          type="button"
                          onClick={() => onOpenNote?.(String(tooth))}
                          data-testid={`tooth-${tooth}-note-edit`}
                          title="Edit note"
                          className="text-slate-400 hover:text-teal-600"
                        ><Pencil className="w-3 h-3" /></button>
                        <button
                          type="button"
                          onClick={() => onSetToothNote?.(String(tooth), null)}
                          data-testid={`tooth-${tooth}-note-clear`}
                          title="Clear note"
                          className="text-slate-400 hover:text-rose-600"
                        ><XIcon className="w-3 h-3" /></button>
                      </>
                    ) : null}
                  </div>
                ) : (canEdit ? (
                  <div className="px-3 py-1.5 border-b border-slate-100">
                    <button
                      type="button"
                      onClick={() => onOpenNote?.(String(tooth))}
                      data-testid={`tooth-${tooth}-note-add`}
                      className="text-[11px] font-medium text-slate-400 hover:text-teal-600 inline-flex items-center gap-1"
                    >
                      <MessageSquare className="w-3 h-3" /> Add note for this tooth
                    </button>
                  </div>
                ) : null)
              ) : null}

              <ul className="divide-y divide-slate-100">
                {items.map((it) => {
                  const proc = procedures?.find((p) => p.id === it.procedure_id);
                  return (
                    <ItemRow
                      key={it.id}
                      item={it}
                      busy={busy.item?.[it.id]}
                      isDraft={isDraft}
                      isProposed={isProposed}
                      canAccept={canAccept}
                      canEdit={canEdit}
                      showPhasePicker={activePhase === "all" && canEdit && groupPhase === "__mixed"}
                      phaseNumbers={phaseNumbers}
                      phaseNames={plan?.phase_names ?? {}}
                      onSetPhase={(n) => onSetItemPhase?.(it.id, n)}
                      onDelete={() => onDeleteItem?.(it.id)}
                      onAccept={() => onSetItemStatus?.(it.id, "Accepted")}
                      onDefer={()  => onSetItemStatus?.(it.id, "Deferred")}
                      onDecline={() => setDeclineFor(it.id)}
                      onSchedule={() => onScheduleItem?.(it)}
                      onUnschedule={() => onUnscheduleItem?.(it.id)}
                      onSetDiscount={onSetItemDiscount}
                      onSetPrice={onSetItemPrice}
                      onSetNote={onSetItemNote}
                      patient={patient}
                      procedure={proc}
                      onVisitStagesChanged={onVisitStagesChanged}
                    />
                  );
                })}
              </ul>
            </div>
            );
          })
        )}

        {/* Amendments — rendered separately from originals (per spec).
            Only shown on the "All" tab; amendments don't carry a phase
            number in the same sense as originals. Visually distinct
            (amber tint + badge) so doctors and the receipt PDF can tell
            at a glance what changed after acceptance. */}
        {activePhase === "all" && amendmentItems.length > 0 ? (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50/40 overflow-hidden"
            data-testid="tx-plan-amendments"
          >
            <div className="bg-amber-100/60 px-3 py-2 flex items-baseline justify-between gap-2">
              <div className="flex items-baseline gap-2">
                <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-600 text-white">
                  Amendments
                </span>
                <span className="text-xs text-amber-900">
                  {amendmentItems.length} change{amendmentItems.length === 1 ? "" : "s"} after acceptance
                </span>
              </div>
            </div>
            <ul className="divide-y divide-amber-200/60">
              {amendmentItems.map((a) => {
                const fee = Number(a.fee_centavos || 0);
                const isCredit = fee < 0;
                // A folded walk-in line — added to the plan at checkout, not a
                // clinical amendment. Tagged by unfold-able marker on the notes.
                const isFold = typeof a.amendment_notes === "string"
                  && a.amendment_notes.startsWith("fold:walk_in");
                const isRemoved = a.status === "Cancelled";
                // Reason pill: folded lines read "added to plan", not the raw
                // patient_requested_change reason code.
                const reason = isFold
                  ? "added to plan"
                  : (a.amendment_reason_code || "").replace(/_/g, " ");
                const canUndoFold = isFold && !isRemoved && canEdit && !readOnlyActions;
                const when = a.amended_at ? new Date(a.amended_at).toLocaleDateString(undefined, {
                  month: "short", day: "numeric", year: "2-digit",
                  hour: "numeric", minute: "2-digit",
                }) : null;
                return (
                  <li
                    key={a.id}
                    data-testid={`tx-plan-amendment-${a.id}`}
                    className="px-3 py-2 flex items-baseline justify-between gap-3 text-[13px]"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={[
                          "font-medium truncate",
                          isRemoved ? "text-slate-400 line-through" : "text-slate-900",
                        ].join(" ")}>
                          {a.name_snapshot || a.procedure_name || a.display_label || "Amendment"}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold">
                          {reason}
                        </span>
                        {a.tooth_number ? (
                          <span className="text-[10px] text-slate-500 font-mono">#{a.tooth_number}</span>
                        ) : null}
                        {isFold && isRemoved ? (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold">
                            Removed from plan
                          </span>
                        ) : null}
                      </div>
                      {a.amendment_notes && !isFold ? (
                        <p className="text-[11px] text-slate-600 mt-0.5 italic">"{a.amendment_notes}"</p>
                      ) : null}
                      {when ? (
                        <p className="text-[10px] text-slate-500 mt-0.5">{when}</p>
                      ) : null}
                      {canUndoFold ? (
                        <button
                          type="button"
                          onClick={() => setUnfoldTarget(a)}
                          data-testid={`tx-plan-unfold-${a.id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-rose-700"
                        >
                          <Undo2 className="w-3 h-3" />
                          Undo — remove from plan
                        </button>
                      ) : null}
                    </div>
                    <span className={[
                      "shrink-0 font-semibold tabular-nums",
                      isRemoved ? "text-slate-400 line-through" : isCredit ? "text-rose-700" : "text-amber-800",
                    ].join(" ")}>
                      {fee > 0 ? "+" : ""}{formatPHP(fee)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      {/* Undo-fold confirm — removing a walk-in line that was added to the
          plan at checkout. Cancels its plan charge (append-only reversal);
          the work is no longer billed on the plan. Server blocks this once
          any payment has landed on the fold. */}
      {unfoldTarget ? (
        <div
          role="dialog" aria-modal="true"
          data-testid="tx-plan-unfold-dialog"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !unfolding) setUnfoldTarget(null); }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Undo2 className="w-4 h-4 text-slate-500" strokeWidth={2.25} />
                Remove from plan?
              </h2>
              <button type="button" onClick={() => !unfolding && setUnfoldTarget(null)}
                className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-2 text-[13px] text-slate-700">
              <p>
                <strong className="text-slate-900">
                  {unfoldTarget.name_snapshot || unfoldTarget.procedure_name || unfoldTarget.display_label || "This work"}
                </strong>
                {" "}was added to this plan at checkout
                {Number(unfoldTarget.fee_centavos || 0) !== 0 ? (
                  <> ({formatPHP(Number(unfoldTarget.fee_centavos || 0))})</>
                ) : null}.
              </p>
              <p>
                Removing it cancels that charge on the plan — the work will
                no longer be billed here. If the patient should still pay for
                it, add it back as a separate charge.
              </p>
              <p className="text-[11px] text-slate-500">
                Only possible before it's been paid.
              </p>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3">
              <button type="button" onClick={() => setUnfoldTarget(null)}
                disabled={unfolding}
                className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded">
                Keep on plan
              </button>
              <button type="button"
                onClick={() => performUnfold(unfoldTarget)}
                disabled={unfolding}
                data-testid="tx-plan-unfold-confirm"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded disabled:opacity-60">
                {unfolding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                Remove from plan
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Footer total + send */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
        <div className="flex items-baseline gap-3" data-tutorial-id="doctor-tx-plan-estimate">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Total</span>
          <span className="text-2xl font-bold text-slate-900 tabular-nums">{formatPHP(totals.openTotal)}</span>
          {totals.declinedTotal > 0 ? (
            <span className="text-xs text-slate-400">
              (declined: {formatPHP(totals.declinedTotal)})
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!noItems ? (
            <button
              type="button"
              onClick={() => onDownloadPdf?.(plan.id)}
              disabled={readOnlyActions || blockProposeOnPhases}
              data-testid="tx-plan-download-pdf"
              title={readOnlyActions
                ? "Tutorial demo only. No PDF is generated."
                : blockProposeOnPhases
                ? `Place all items into appointments first (${unassignedCount} need appointment placement).`
                : "Download a PDF preview of this plan"}
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Download PDF
            </button>
          ) : null}
          {canSignDocument ? (
            <button
              type="button"
              onClick={() => onSignDocument?.(plan)}
              disabled={signatureBusy || blockProposeOnPhases}
              data-testid="tx-plan-sign-document"
              title={blockProposeOnPhases
                ? `Place all items into appointments first (${unassignedCount} need appointment placement).`
                : isProposed
                  ? "Capture patient signature and accept this plan"
                  : "Capture patient signature for this plan"}
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-teal-200 text-teal-800 bg-teal-50 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {signatureBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />}
              Sign
            </button>
          ) : isSignedDocument ? (
            <span
              data-testid="tx-plan-signed-badge"
              className="px-2.5 py-2 text-xs font-semibold rounded-lg border border-emerald-200 text-emerald-800 bg-emerald-50 inline-flex items-center gap-1.5"
              title="This document has a saved patient signature"
            >
              <Check className="w-3.5 h-3.5" />
              Signed
            </span>
          ) : null}
          {canCompare && !noItems ? (
            <button
              type="button"
              onClick={() => onComparePlans?.(plan.id)}
              disabled={readOnlyActions || blockProposeOnPhases}
              data-testid="tx-plan-compare"
              title={readOnlyActions
                ? "Tutorial demo only. No comparison PDF is generated."
                : blockProposeOnPhases
                ? `Place all items into appointments first (${unassignedCount} need appointment placement).`
                : "Generate a side-by-side comparison PDF with another plan"}
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <GitCompareArrows className="w-3.5 h-3.5" />
              Compare
            </button>
          ) : null}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="tx-plan-undo-redo">
            <button
              type="button"
              onClick={onUndo}
              disabled={undoBusy || undoCount === 0}
              data-testid="tx-plan-undo"
              title={`Undo last action (${navigator.platform?.match(/Mac/i) ? "⌘Z" : "Ctrl+Z"})`}
              className="px-2.5 py-2 text-slate-600 hover:bg-slate-50 disabled:text-slate-300 disabled:cursor-not-allowed border-r border-slate-200"
            >
              {undoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={onRedo}
              disabled={undoBusy || redoCount === 0}
              data-testid="tx-plan-redo"
              title={`Redo (${navigator.platform?.match(/Mac/i) ? "⌘⇧Z" : "Ctrl+Y"})`}
              className="px-2.5 py-2 text-slate-600 hover:bg-slate-50 disabled:text-slate-300 disabled:cursor-not-allowed"
            >
              <Redo2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {isDraft && canPropose ? (
            <button
              type="button"
              onClick={() => onPropose?.(plan.id)}
              disabled={readOnlyActions || busy.propose || noItems || blockProposeOnPhases}
              data-testid="tx-plan-propose"
              data-tutorial-id="doctor-tx-plan-acceptance"
              title={
                readOnlyActions      ? "Tutorial demo only. No plan is sent to the patient."
                : noItems            ? "Add at least one item before proposing"
                : blockProposeOnPhases ? `Place all items into appointments first (${unassignedCount} need appointment placement)`
                                       : "Send to patient"
              }
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
            >
              {busy.propose ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send to patient (Propose)
            </button>
          ) : null}
        </div>
      </div>

      {/* Decline reason modal */}
      {declineFor != null ? (
        <DeclineReasonDialog
          reason={declineReason}
          onChange={setDeclineReason}
          onConfirm={() => {
            onSetItemStatus?.(declineFor, "Declined", declineReason.trim() || null);
            setDeclineFor(null);
            setDeclineReason("");
          }}
          onClose={() => { setDeclineFor(null); setDeclineReason(""); }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// VisitChainWires — node-editor style overlay. Draws a bezier "wire"
// between consecutive visits of the same multi-visit procedure, so the
// chain reads at a glance whether the visits are stacked in one
// appointment or split across columns. It measures the real card DOM
// positions (elements tagged data-chain-key) relative to the scrolling
// board and re-measures on scroll / resize / data change, so the wires
// stay attached no matter how the board moves or reflows.
// ---------------------------------------------------------------------
function VisitChainWires({ wires, colorByItem }) {
  const svgRef = useRef(null);
  const [paths, setPaths] = useState([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    // Measure against the board scroll container — our own parent. Reading
    // svgRef.parentElement (rather than a passed-in ref) avoids the
    // child-effect-runs-before-parent-ref-attaches ordering trap.
    const container = svgRef.current?.parentElement;
    if (!container) return undefined;
    let raf = 0;

    const measure = () => {
      const cRect = container.getBoundingClientRect();
      const rects = new Map();
      container.querySelectorAll("[data-chain-key]").forEach((node) => {
        const r = node.getBoundingClientRect();
        const left = r.left - cRect.left + container.scrollLeft;
        const top = r.top - cRect.top + container.scrollTop;
        rects.set(node.getAttribute("data-chain-key"), {
          left,
          right: left + r.width,
          top,
          bottom: top + r.height,
          midX: left + r.width / 2,
          midY: top + r.height / 2,
        });
      });

      const next = [];
      for (const w of wires) {
        const a = rects.get(w.fromKey);
        const b = rects.get(w.toKey);
        if (!a || !b) continue;
        const color = colorByItem[w.itemId] || "#7F77DD";
        // Same column (b sits below a): route bottom-port -> top-port.
        // Otherwise (split across appointments): right-port -> left-port.
        const sameColumn = Math.abs(a.left - b.left) < 12 && b.top >= a.top - 4;
        let d, x1, y1, x2, y2;
        if (sameColumn) {
          x1 = a.midX; y1 = a.bottom; x2 = b.midX; y2 = b.top;
          const dy = Math.max(16, (y2 - y1) * 0.6);
          d = `M ${x1} ${y1} C ${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`;
        } else {
          x1 = a.right; y1 = a.midY; x2 = b.left; y2 = b.midY;
          const dx = Math.max(36, Math.abs(x2 - x1) * 0.5);
          d = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
        }
        next.push({ key: `${w.fromKey}->${w.toKey}`, d, x1, y1, x2, y2, color });
      }
      setPaths(next);
      setSize({ w: container.scrollWidth, h: container.scrollHeight });
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(container);
    container.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      container.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [wires, colorByItem]);

  return (
    <svg
      ref={svgRef}
      aria-hidden="true"
      data-testid="visit-chain-wires"
      style={{ position: "absolute", top: 0, left: 0, width: size.w, height: size.h, pointerEvents: "none", overflow: "visible", zIndex: 0 }}
    >
      {paths.map((p) => (
        <g key={p.key}>
          <path d={p.d} fill="none" stroke={p.color} strokeWidth="2" strokeOpacity="0.65" strokeLinecap="round" />
          <circle cx={p.x1} cy={p.y1} r="3.5" fill={p.color} />
          <circle cx={p.x2} cy={p.y2} r="3.5" fill={p.color} />
        </g>
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------
// PlanItemPriceList — the "quote" lens under the appointment board: one
// row per procedure line with editable price + discount, the net after
// discount, and plan totals. Expand a row to see (read-only) which
// appointment each visit sits in. The board stays the scheduling surface;
// this is the money view.
// ---------------------------------------------------------------------
function pesosFmt(cents) {
  return `₱${Math.round(Number(cents || 0) / 100).toLocaleString()}`;
}

function PlanQuoteRow({ item, phaseNames = {}, canEdit, isDraft, onSetItemPrice, onSetItemDiscount }) {
  const [open, setOpen] = useState(false);
  const [priceBusy, setPriceBusy] = useState(false);
  const [discountBusy, setDiscountBusy] = useState(false);
  const visits = Array.isArray(item.procedure_visits)
    ? [...item.procedure_visits].sort((a, b) => Number(a.sequence) - Number(b.sequence))
    : [];
  const name = item.display_label || item.procedure_name || "Procedure";
  const tooth = item.tooth_number ? `#${item.tooth_number}` : "";
  const meta = [tooth, visits.length > 1 ? `${visits.length} visits` : null].filter(Boolean).join(" · ");
  const gross = Number(item.fee_centavos || 0);
  const net = treatmentPlanItemNetCentavos(item);
  const discount = Math.max(0, gross - net);
  const editable = canEdit && isDraft;
  const canExpand = visits.length > 0;

  async function savePrice(cents) {
    setPriceBusy(true);
    try { await onSetItemPrice?.(item.id, cents); } finally { setPriceBusy(false); }
  }
  async function saveDiscount(cents) {
    setDiscountBusy(true);
    try { await onSetItemDiscount?.(item.id, { pct: null, amount: cents, reason: null }); } finally { setDiscountBusy(false); }
  }

  return (
    <div data-testid={`tx-list-row-${item.id}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => canExpand && setOpen((o) => !o)}
          disabled={!canExpand}
          aria-label="Show visit breakdown"
          className={`shrink-0 text-slate-400 ${canExpand ? "hover:text-slate-700" : "opacity-0"}`}
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 truncate">{name}</p>
          {meta ? <p className="text-[11px] text-slate-500">{meta}</p> : null}
        </div>

        {/* Price */}
        <div className="shrink-0">
          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Price</p>
          {editable && onSetItemPrice ? (
            <label className="flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-1 cursor-text focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-200" title="Edit fee">
              <span className="font-bold tabular-nums text-slate-400 text-[11px]">₱</span>
              <input
                type="number" inputMode="numeric" min={0} step={100}
                defaultValue={Math.round(gross / 100)} disabled={priceBusy}
                data-testid={`tx-list-row-${item.id}-price`}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                onBlur={(e) => { const p = Math.max(0, Math.round(Number(e.target.value) || 0)); const c = p * 100; if (c !== gross) savePrice(c); else e.target.value = String(Math.round(gross / 100)); }}
                className="w-16 bg-transparent font-bold tabular-nums text-slate-900 outline-none disabled:opacity-40"
              />
              {priceBusy ? <Loader2 className="w-3 h-3 animate-spin text-slate-400" /> : <Pencil className="w-3 h-3 text-slate-400" />}
            </label>
          ) : (
            <span className="text-sm font-bold tabular-nums text-slate-900">{pesosFmt(gross)}</span>
          )}
        </div>

        {/* Discount */}
        <div className="shrink-0">
          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Discount</p>
          {editable && onSetItemDiscount ? (
            <label className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 cursor-text focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-200" title="Discount off this fee">
              <span className="font-bold tabular-nums text-slate-400 text-[11px]">−₱</span>
              <input
                type="number" inputMode="numeric" min={0} step={100}
                defaultValue={discount ? Math.round(discount / 100) : ""} placeholder="0" disabled={discountBusy}
                data-testid={`tx-list-row-${item.id}-discount`}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                onBlur={(e) => { const p = Math.max(0, Math.round(Number(e.target.value) || 0)); const c = p * 100; if (c !== discount) saveDiscount(c); }}
                className="w-14 bg-transparent font-medium tabular-nums text-slate-700 outline-none disabled:opacity-40 placeholder:text-slate-300"
              />
              {discountBusy ? <Loader2 className="w-3 h-3 animate-spin text-slate-400" /> : null}
            </label>
          ) : (
            discount > 0
              ? <span className="text-sm text-slate-500 tabular-nums">−{pesosFmt(discount)}</span>
              : <span className="text-sm text-slate-300">—</span>
          )}
        </div>

        {/* Net */}
        <div className="shrink-0 w-20 text-right">
          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Net</p>
          <p className="text-sm font-bold tabular-nums text-slate-900">{pesosFmt(net)}</p>
        </div>
      </div>

      {open && canExpand ? (
        <div className="px-3 pb-2 pl-10 space-y-0.5" data-testid={`tx-list-row-${item.id}-visits`}>
          {visits.map((v) => {
            const phase = Number(v.phase_number);
            const appt = phase >= 1 ? (phaseNames?.[String(phase)] || `Appointment ${phase}`) : "Needs appointment";
            return (
              <div key={v.id} className="flex items-center gap-2 text-[11px] text-slate-500">
                <span className="font-medium text-slate-600">Visit {v.sequence}</span>
                <span className="truncate">{v.template_label || "—"}</span>
                <span className="ml-auto text-slate-400">{appt}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function PlanItemPriceList({ items = [], phaseNames = {}, canEdit = false, isDraft = false, onSetItemPrice, onSetItemDiscount }) {
  const rows = (items || []).filter((it) => it.status !== "Declined" && it.status !== "Cancelled");
  if (rows.length === 0) return null;
  const grossTotal = rows.reduce((s, it) => s + Number(it.fee_centavos || 0), 0);
  const netTotal = rows.reduce((s, it) => s + treatmentPlanItemNetCentavos(it), 0);
  const discountTotal = Math.max(0, grossTotal - netTotal);
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white" data-testid="tx-plan-item-list">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Line items</p>
        <p className="text-[11px] text-slate-400">Quote</p>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((it) => (
          <PlanQuoteRow
            key={it.id}
            item={it}
            phaseNames={phaseNames}
            canEdit={canEdit}
            isDraft={isDraft}
            onSetItemPrice={onSetItemPrice}
            onSetItemDiscount={onSetItemDiscount}
          />
        ))}
      </div>
      <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-end gap-4 text-[12px]" data-testid="tx-plan-item-list-totals">
        <span className="text-slate-500">Subtotal <span className="font-medium tabular-nums text-slate-700">{pesosFmt(grossTotal)}</span></span>
        {discountTotal > 0 ? (
          <span className="text-slate-500">Discount <span className="font-medium tabular-nums text-slate-700">−{pesosFmt(discountTotal)}</span></span>
        ) : null}
        <span className="font-semibold text-slate-900">Total <span className="tabular-nums">{pesosFmt(netTotal)}</span></span>
      </div>
    </div>
  );
}

function TxAppointmentBoard({
  items = [],
  procedures = [],
  procedureColorMap = null,
  phaseNumbers = [],
  phaseNames = {},
  phaseChair = {},
  phaseCalendar = {},
  busy = {},
  canEdit = false,
  isDraft = false,
  sequenceNudges = [],
  sequenceBusyItemId = null,
  onApplyVisitSequence,
  onSetItemPhase,
  onSetItemPrice,
  onDeleteItem,
  onSetVisitPhase,
  onVisitStagesChanged,
  onSelectUnassigned,
}) {
  const procedureById = useMemo(() => {
    const map = new Map();
    for (const proc of procedures || []) {
      if (proc?.id) map.set(proc.id, proc);
    }
    return map;
  }, [procedures]);

  const activeItems = useMemo(
    () => (items || []).filter((item) => !item.is_amendment && !["Cancelled", "Declined"].includes(item.status)),
    [items],
  );
  const lanes = useMemo(() => {
    const unassigned = [];
    const byPhase = new Map(phaseNumbers.map((n) => [n, []]));
    for (const item of activeItems) {
      const visits = Array.isArray(item.procedure_visits) ? [...item.procedure_visits] : [];
      visits.sort((a, b) => Number(a.sequence) - Number(b.sequence));
      const entries = visits.length > 0
        ? visits.map((visit) => ({ item, visit }))
        : [{ item, visit: null }];
      for (const entry of entries) {
        const phase = toPhaseNum(entry.visit?.phase_number ?? item.phase_number);
        if (!phase) {
          unassigned.push(entry);
        } else if (byPhase.has(phase)) {
          byPhase.get(phase).push(entry);
        } else {
          byPhase.set(phase, [entry]);
        }
      }
    }
    return { unassigned, byPhase };
  }, [activeItems, phaseNumbers]);

  // Node-editor wires: one per consecutive visit pair of a multi-visit
  // procedure. The overlay measures the tagged cards and draws them.
  const chainWires = useMemo(() => buildVisitChainWires(activeItems), [activeItems]);
  const chainColorByItem = useMemo(() => {
    const map = {};
    for (const item of activeItems) {
      map[item.id] = procedureColor(
        procedureById.get(item.procedure_id) || { category: item.category, color_hex: item.color_hex },
        procedureColorMap,
      );
    }
    return map;
  }, [activeItems, procedureById, procedureColorMap]);

  const laneMeta = (key, laneEntries) => {
    const chair = laneEntries.reduce((sum, entry) => {
      if (entry.visit) return sum + (Number(entry.visit.chair_min) || 0);
      return sum + itemChairMinutes(entry.item);
    }, 0);
    const phaseNumber = toPhaseNum(key);
    // The dentist's typed appointment duration wins over the summed chair
    // time; fall back to the phase chair total, then the lane's own sum.
    const planned = phaseNumber ? Number(phaseCalendar?.[String(phaseNumber)]?.duration_min) : NaN;
    const chairTotal = phaseNumber && Number.isFinite(Number(phaseChair?.[String(phaseNumber)]))
      ? Number(phaseChair[String(phaseNumber)])
      : chair;
    const appointmentChair = Number.isFinite(planned) && planned > 0 ? planned : chairTotal;
    return { chair: appointmentChair };
  };

    const renderLane = (key, title, laneEntries, tone = "slate") => {
    const meta = laneMeta(key, laneEntries);
    const empty = laneEntries.length === 0;
    const toneClass = tone === "amber"
      ? "border-amber-200 bg-amber-50/45"
      : "border-slate-200 bg-slate-50/55";
    const isUnassignedLane = key === "unassigned";
    return (
      <section
        key={key}
        data-testid={`tx-appointment-lane-${key}`}
        className={`relative z-10 min-w-[260px] rounded-lg border ${toneClass} flex flex-col`}
      >
        <button
          type="button"
          onClick={isUnassignedLane ? onSelectUnassigned : undefined}
          className={`w-full text-left px-3 py-2 border-b border-slate-200/70 flex items-start justify-between gap-3 rounded-t-lg ${
            isUnassignedLane ? "hover:bg-amber-100/60 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-inset" : "cursor-default"
          }`}
          title={isUnassignedLane ? "Show treatment work that needs an appointment" : undefined}
        >
          <div className="min-w-0">
            <h3 className="text-xs font-bold text-slate-800 truncate">{title}</h3>
            <p className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
              {laneEntries.length} work item{laneEntries.length === 1 ? "" : "s"} · {meta.chair} min
            </p>
          </div>
          {tone === "amber" && laneEntries.length > 0 ? (
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-200">
              Assign
            </span>
          ) : null}
        </button>
        <div className="p-2 space-y-2 flex-1">
          {empty ? (
            <button
              type="button"
              onClick={isUnassignedLane ? onSelectUnassigned : undefined}
              disabled={!isUnassignedLane}
              title={isUnassignedLane ? "Show treatment work that needs an appointment" : undefined}
              className={`w-full min-h-[96px] rounded-md border border-dashed border-slate-300/80 bg-white/50 flex items-center justify-center px-3 text-center ${
                isUnassignedLane ? "hover:border-amber-300 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-300" : "cursor-default"
              }`}
            >
              <span className="text-xs text-slate-400">No items</span>
            </button>
          ) : (
            laneEntries.map(({ item, visit }) => (
              <TxBoardCard
                key={`${item.id}:${visit?.id || "item"}`}
                chainKey={`${item.id}:${visit?.id || "item"}`}
                item={item}
                visit={visit}
                procedure={procedureById.get(item.procedure_id)}
                procedureColorMap={procedureColorMap}
                phaseNumbers={phaseNumbers}
                phaseNames={phaseNames}
                busy={busy.item?.[item.id]}
                canEdit={canEdit}
                isDraft={isDraft}
                onSetPhase={(phase) => onSetItemPhase?.(item.id, phase)}
                onSetPrice={onSetItemPrice ? (cents) => onSetItemPrice(item.id, cents) : null}
                onDelete={() => onDeleteItem?.(item.id)}
                onSetVisitPhase={onSetVisitPhase}
                onVisitStagesChanged={onVisitStagesChanged}
              />
            ))
          )}
        </div>
      </section>
    );
  };

  return (
    <div className="space-y-3" data-testid="tx-appointment-board">
      <AppointmentSequenceNudges
        nudges={sequenceNudges}
        phaseNames={phaseNames}
        canEdit={canEdit}
        isDraft={isDraft}
        busyItemId={sequenceBusyItemId}
        onApply={onApplyVisitSequence}
      />
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-bold text-slate-900">Appointment board</h2>
          <p className="text-xs text-slate-500 mt-0.5">Visit sequence</p>
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {lanes.unassigned.length} need appointment · {phaseNumbers.length} appointment{phaseNumbers.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="relative flex items-start gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        <VisitChainWires wires={chainWires} colorByItem={chainColorByItem} />
        {renderLane("unassigned", "Needs appointment", lanes.unassigned, "amber")}
        {phaseNumbers.map((n) => (
          renderLane(
            String(n),
            phaseNames?.[String(n)] || `Appointment ${n}`,
            lanes.byPhase.get(n) || [],
            "slate",
          )
        ))}
      </div>
    </div>
  );
}

function AppointmentSequenceNudges({
  nudges = [],
  phaseNames = {},
  canEdit = false,
  isDraft = false,
  busyItemId = null,
  onApply,
}) {
  if (!nudges.length) return null;

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
      data-testid="tx-appointment-sequence-nudge"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-700" />
            <h3 className="text-xs font-bold text-amber-950">Review appointment sequence</h3>
          </div>
          <div className="mt-1 space-y-1">
            {nudges.map((nudge) => {
              const name = itemDisplayName(nudge.item);
              const current = phaseNames?.[String(nudge.currentPhase)] || `Appointment ${nudge.currentPhase}`;
              const targetSummary = nudge.targetPhases
                .map((phase) => phaseNames?.[String(phase)] || `Appointment ${phase}`)
                .join(" → ");
              return (
                <div
                  key={nudge.item.id}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-900"
                  data-testid={`tx-appointment-sequence-nudge-${nudge.item.id}`}
                >
                  <span>
                    <strong>{name}</strong> has {nudge.visits.length} visits in {current}.
                  </span>
                  <span className="text-amber-700">Suggested: {targetSummary}</span>
                </div>
              );
            })}
          </div>
        </div>
        {isDraft && canEdit ? (
          <div className="flex flex-wrap gap-1.5">
            {nudges.map((nudge) => {
              const canOneClick = visitSequenceChanges(nudge).length === 1;
              if (!canOneClick) {
                return (
                  <span
                    key={nudge.item.id}
                    className="inline-flex items-center rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800"
                  >
                    Use dropdowns
                  </span>
                );
              }
              return (
                <button
                  key={nudge.item.id}
                  type="button"
                  onClick={() => onApply?.(nudge)}
                  disabled={busyItemId === nudge.item.id}
                  data-testid={`tx-appointment-sequence-apply-${nudge.item.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-amber-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 disabled:opacity-50"
                >
                  {busyItemId === nudge.item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calendar className="h-3 w-3" />}
                  Split visits
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TxBoardCard({
  item,
  visit = null,
  chainKey = null,
  procedure,
  procedureColorMap = null,
  phaseNumbers = [],
  phaseNames = {},
  busy,
  canEdit = false,
  isDraft = false,
  onSetPhase,
  onSetPrice,
  onSetVisitPhase,
  onDelete,
  onVisitStagesChanged,
}) {
  const [stageBusy, setStageBusy] = useState(null);
  const [priceBusy, setPriceBusy] = useState(false);
  const visits = Array.isArray(item.procedure_visits) ? [...item.procedure_visits] : [];
  visits.sort((a, b) => Number(a.sequence) - Number(b.sequence));
  const teeth = getItemTeeth(item);
  const unitCount = Math.max(1, Number(item.unit_count) || (teeth.length > 1 ? teeth.length : 1));
  const requiresLab = !!(procedure?.requires_lab_work || item.requires_lab_work || visits.some((v) => Number(v.lab_gap_days) > 0));
  const phaseValue = (visit?.phase_number ?? item.phase_number) == null ? "" : String(visit?.phase_number ?? item.phase_number);
  const hasStages = visits.length >= 2;
  const isStageEntry = !!visit;
  const stageFrozen = !!visit?.completed_at;
  const canDeleteStage = isStageEntry && visits.length > 1 && !stageFrozen;
  const canRemoveCard = isDraft && canEdit && (isStageEntry ? canDeleteStage : Boolean(onDelete));
  // The procedure's fee lives on Visit 1 (or the single / item-level card);
  // later visits of the same case carry no separate charge.
  const firstStageSequence = visits.length > 0 ? Number(visits[0]?.sequence || 0) : 0;
  const isFirstStageEntry = isStageEntry && Number(visit?.sequence || 0) === firstStageSequence;
  const isPriceCard = !isStageEntry || isFirstStageEntry;
  const feeCentavos = Number(item.fee_centavos || 0);
  const nextPhaseNumber = phaseNumbers.length === 0 ? 1 : Math.max(...phaseNumbers) + 1;
  const phaseOptions = isStageEntry
    ? visitPhaseOptions(visit, phaseNumbers)
    : phaseNumbers.map((n) => ({ number: n, disabled: false }));
  const cardColor = procedureColor(procedure || { category: item.category, color_hex: item.color_hex }, procedureColorMap);
  const cardTint = colorWithAlpha(cardColor, "12");
  const cardBorder = colorWithAlpha(cardColor, "66", cardColor);

  async function patchStage(visitId, phaseNumber) {
    setStageBusy(visitId);
    try {
      if (onSetVisitPhase) {
        await onSetVisitPhase(item.id, visitId, phaseNumber ?? null);
        onVisitStagesChanged?.();
        return;
      }
      const { error } = await supabase.rpc("set_procedure_visit_stage", {
        p_visit_id: visitId,
        // 0 = unassign (Needs appointment); a real appointment is >= 1.
        // Matches the item-level set_plan_item_phase sentinel.
        p_phase_number: phaseNumber == null ? 0 : phaseNumber,
      });
      if (error) { toast.error(error.message ?? "Could not update visit stage."); return; }
      onVisitStagesChanged?.();
    } finally {
      setStageBusy(null);
    }
  }

  async function addStage() {
    setStageBusy("add");
    try {
      const { error } = await supabase.rpc("add_procedure_visit_stage", {
        p_item_id: item.id,
        p_label: `Stage ${visits.length + 1}`,
        p_chair_min: 30,
        p_lab_gap_days: null,
        p_phase_number: null,
      });
      if (error) { toast.error(error.message ?? "Could not add stage."); return; }
      onVisitStagesChanged?.();
    } finally {
      setStageBusy(null);
    }
  }

  async function deleteStage(visitId) {
    if (visits.length <= 1) {
      toast.error("Add another stage before deleting this one.");
      return;
    }
    if (!confirm("Delete this visit stage? The remaining stages will renumber.")) return;
    setStageBusy(visitId);
    try {
      const { error } = await supabase.rpc("delete_procedure_visit_stage", {
        p_visit_id: visitId,
      });
      if (error) { toast.error(error.message ?? "Could not delete stage."); return; }
      onVisitStagesChanged?.();
    } finally {
      setStageBusy(null);
    }
  }

  async function savePrice(cents) {
    if (!onSetPrice) return;
    setPriceBusy(true);
    try {
      await onSetPrice(cents);
    } finally {
      setPriceBusy(false);
    }
  }

  async function patchStageLabel(visitId, label) {
    setStageBusy(visitId);
    try {
      const { error } = await supabase.rpc("set_procedure_visit_stage", {
        p_visit_id: visitId,
        p_label: label,
      });
      if (error) { toast.error(error.message ?? "Could not rename the stage."); return; }
      onVisitStagesChanged?.();
    } finally {
      setStageBusy(null);
    }
  }

  function handlePhaseSelectValue(value) {
    if (value === "__add_stage") {
      addStage();
      return;
    }
    const nextPhase = value === "__new" ? nextPhaseNumber : value === "" ? null : Number(value);
    if (isStageEntry) {
      patchStage(visit.id, nextPhase);
    } else {
      onSetPhase?.(nextPhase);
    }
  }

  return (
    <article
      data-testid={`tx-board-card-${item.id}`}
      data-chain-key={chainKey || undefined}
      data-tutorial-id={visit?.tutorial_id || ((!visit || Number(visit.sequence) === 1) ? (item.tutorial_id || undefined) : undefined)}
      data-linked-item-id={item.id}
      className="rounded-md border shadow-[0_1px_0_rgba(15,23,42,0.03)] overflow-hidden transition-colors"
      style={{
        borderColor: cardBorder,
        backgroundColor: cardTint,
        boxShadow: `0 0 0 1px ${colorWithAlpha(cardColor, "18")}, 0 1px 2px rgba(15,23,42,0.04)`,
      }}
    >
      <div className="px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: cardColor }}
                aria-hidden="true"
              />
              <p
                className="text-[13px] font-semibold text-slate-900 truncate"
                data-tutorial-id={isStageEntry && Number(visit?.sequence || 0) === 1 ? (item.tutorial_id || undefined) : undefined}
              >
                {item.display_label || item.procedure_name || "Procedure"}
              </p>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
              {isStageEntry && hasStages ? (
                <span className="inline-flex items-center gap-1 rounded bg-slate-800 px-1.5 py-0.5 font-bold text-white">
                  Visit {visit.sequence} of {visits.length}
                </span>
              ) : null}
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">
                {toothSummary(item)}
              </span>
              {unitCount > 1 ? <span>{unitCount} units</span> : null}
              {requiresLab ? (
                <span className="inline-flex items-center gap-1 text-violet-700">
                  <FlaskConical className="w-3 h-3" /> lab
                </span>
              ) : null}
            </div>
          </div>
          {canRemoveCard ? (
            <button
              type="button"
              onClick={isStageEntry ? () => deleteStage(visit.id) : onDelete}
              disabled={busy || (isStageEntry && stageBusy === visit?.id)}
              aria-label={isStageEntry ? "Remove this stage" : "Remove from plan"}
              title={isStageEntry ? "Remove this stage" : "Remove from plan"}
              data-testid={isStageEntry ? `tx-board-card-${item.id}-stage-v${visit.sequence}-delete` : undefined}
              className="shrink-0 p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40"
            >
              {busy || (isStageEntry && stageBusy === visit?.id)
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          ) : null}
        </div>

        {/* Vertical form: Stage → Price → Appointment, each labelled. */}
        <div className="mt-2 space-y-2 text-[12px]">

          {/* Stage */}
          {isStageEntry ? (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Stage</p>
              {canEdit && isDraft && !stageFrozen ? (
                <input
                  type="text"
                  defaultValue={visit.template_label || ""}
                  placeholder="e.g. Impression"
                  disabled={stageBusy === visit?.id}
                  data-testid={`tx-board-card-${item.id}-stage-v${visit.sequence}-label`}
                  title="Name this stage — shows on the patient's plan and PDF"
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    // A stage always keeps a name — an empty box reverts to
                    // the saved label rather than blanking it in the DB.
                    if (!next) { e.target.value = visit.template_label || ""; return; }
                    if (next !== (visit.template_label || "")) patchStageLabel(visit.id, next);
                  }}
                  className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 font-medium text-slate-900 outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-200 placeholder:text-slate-400 placeholder:font-normal disabled:opacity-40"
                />
              ) : (
                <p className="font-medium text-slate-900 truncate">
                  {visit?.template_label || (hasStages ? `${visits.length} stages` : "—")}
                </p>
              )}
            </div>
          ) : null}

          {/* Price — anchored to Visit 1. Later visits of a multi-visit case
              show a clear "no separate charge" indicator instead. */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Price</p>
            {isPriceCard ? (
              (canEdit && isDraft && onSetPrice) ? (
                <label
                  className="flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 cursor-text focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-200"
                  title="Edit this procedure's fee"
                >
                  <span className="font-bold tabular-nums text-slate-400 text-[11px] shrink-0">₱</span>
                  <input
                    // key on the fee so an uncontrolled input re-inits when the
                    // fee changes externally (e.g. a surface is added on the
                    // chart) — otherwise it shows the stale initial value until
                    // the card happens to remount (on appointment assignment).
                    key={`price-${item.id}-${feeCentavos}`}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={100}
                    defaultValue={Math.round(feeCentavos / 100)}
                    disabled={priceBusy}
                    data-testid={`tx-board-card-${item.id}-price`}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    onBlur={(e) => {
                      const pesos = Math.max(0, Math.round(Number(e.target.value) || 0));
                      const cents = pesos * 100;
                      if (cents !== feeCentavos) savePrice(cents);
                      else e.target.value = String(Math.round(feeCentavos / 100));
                    }}
                    className="w-full min-w-0 bg-transparent font-bold tabular-nums text-slate-900 outline-none disabled:opacity-40"
                  />
                  {priceBusy
                    ? <Loader2 className="w-3 h-3 shrink-0 animate-spin text-slate-400" />
                    : <Pencil className="w-3 h-3 shrink-0 text-slate-400" />}
                </label>
              ) : (
                <div className="rounded bg-slate-50 px-2 py-1">
                  <p className="font-bold tabular-nums text-slate-900">
                    ₱{Math.round(feeCentavos / 100).toLocaleString()}
                  </p>
                  {hasStages ? <p className="text-[10px] text-slate-500">whole case</p> : null}
                </div>
              )
            ) : (
              <div className="rounded bg-slate-50 px-2 py-1 text-slate-500">
                <p className="text-[11px] font-medium text-slate-600">No separate charge</p>
                <p className="text-[10px] text-slate-400">part of this case</p>
              </div>
            )}
          </div>

          {/* Appointment # */}
          {phaseNumbers.length > 0 && canEdit ? (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Appointment</p>
              <select
                value={phaseValue}
                onChange={(e) => handlePhaseSelectValue(e.target.value)}
                disabled={busy || stageBusy === visit?.id}
                title={isStageEntry ? "Assign this treatment step to an appointment" : "Assign this treatment item to an appointment"}
                data-testid={`tx-board-card-${item.id}${isStageEntry ? `-stage-v${visit.sequence}` : ""}-phase`}
                data-tutorial-id={isStageEntry && visit?.tutorial_id ? `${visit.tutorial_id}-phase` : undefined}
                className="w-full text-[11px] font-semibold rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-700 hover:border-slate-400 disabled:opacity-40"
              >
                <option value="">Needs appointment</option>
                {phaseOptions.map(({ number, disabled }) => (
                  <option key={number} value={number} disabled={disabled}>
                    {phaseNames?.[String(number)] || `Appointment ${number}`}
                  </option>
                ))}
                {isStageEntry ? (
                  <option value="__add_stage">+ Add stage</option>
                ) : (
                  <option value="__new">+ New appointment</option>
                )}
              </select>
            </div>
          ) : null}
        </div>
      </div>

    </article>
  );
}

// ---------------------------------------------------------------------
// One row in the items list.
// ---------------------------------------------------------------------
function ItemRow({
  item, busy, isDraft, isProposed, canAccept, canEdit,
  showPhasePicker = false, phaseNumbers = [], phaseNames = {},
  onDelete, onAccept, onDefer, onDecline, onSchedule, onUnschedule, onSetPhase,
  onSetDiscount, onSetPrice, onSetNote,
  patient = null, procedure = null,
  onVisitStagesChanged = null,
}) {
  const declined = item.status === "Declined";
  const completed = item.status === "Completed";
  const canSchedule   = canAccept && item.status === "Accepted";
  const canUnschedule = canAccept && item.status === "Scheduled";
  const phaseValue = item.phase_number == null ? "" : String(item.phase_number);

  // Execution mode: item is past acceptance and the dentist is now
  // doing the work. The State zone collapses into the embedded
  // VisitTimelineStrip; we no longer need the redundant CANDIDATE /
  // status pill + verb row because the timeline IS the source of
  // truth for "where is this procedure at?". Cancelled / Declined
  // items keep the legacy pill display because they're terminal.
  const isExecutionMode =
    item.status === "Accepted" || item.status === "Scheduled" || item.status === "Completed";

  // Per-line edit controls. Discount works on any plan status; price
  // and note are Draft-only (server-enforced). Completed / Declined
  // items aren't editable — terminal states for the line.
  const lineEditable     = !!canEdit && !declined && !completed;
  const surfaces         = splitSurfacesTx(item.surface);
  const teethArr         = Array.isArray(item.teeth)
    ? item.teeth.map((t) => (typeof t === "object" ? t?.tooth_number : t)).filter(Boolean)
    : [];
  const unitCount        = Math.max(1, Number(item.unit_count) || (teethArr.length > 1 ? teethArr.length : 1));
  const showUnitMath     = unitCount > 1 && Number(item.fee_centavos || 0) > 0;
  const surfaceCount     = Math.max(1, surfaces.length || 1);
  const unitPriceCents   = surfaceCount > 0
    ? Math.round(Number(item.fee_centavos || 0) / surfaceCount)
    : 0;
  const showSurfaceMath  = surfaceCount > 1 && unitPriceCents > 0;
  const showLineDiscount = hasLineDiscount(item);

  // Visit-time data for the Cost zone.
  const visits = Array.isArray(item.procedure_visits) ? item.procedure_visits : [];
  const singleVisit = visits.length === 1 ? visits[0] : null;
  const totalChairMin = visits.reduce((s, v) => s + (Number(v.chair_min) || 0), 0);
  const canEditTime = lineEditable && isDraft && !!singleVisit;

  // Chair-time breakdown. Visit chair_min is raw procedure work only; setup is
  // added once at the appointment/phase level and must not appear per line.
  const timeBreakdown = (() => {
    if (!singleVisit) {
      return visits.length >= 2
        ? `Σ across ${visits.length} stages`
        : null;
    }
    if (!procedure) return null;
    const raw = Number(procedure.suggested_duration_minutes || 0);
    if (raw <= 0) return null;
    const isSurface = procedure.unit_basis === "surface";
    const units = isSurface ? Math.max(1, surfaceCount) : 1;
    const computed = raw * units;
    const stored = singleVisit.chair_min;
    const overridden = stored != null && stored !== computed;
    const unitLabel = isSurface
      ? (units === 1 ? "1 surface" : `${units} surfaces`)
      : null;
    const workPart = unitLabel ? `${raw} × ${unitLabel}` : `${raw} min`;
    if (overridden) {
      return `manual override · auto work = ${workPart}`;
    }
    return unitLabel ? workPart : null;
  })();

  // Money breakdown text — twin to time. "₱1,500 × 4 surfaces" when
  // multi-surface, otherwise null (no breakdown needed for single).
  const moneyBreakdown = showSurfaceMath
    ? `${formatPHP(unitPriceCents)} × ${surfaceCount} surfaces`
    : showUnitMath
      ? `${formatPHP(Math.round(Number(item.fee_centavos || 0) / unitCount))} × ${unitCount} units`
    : null;

  // Action verbs for the State zone — only render when applicable to
  // the current item status. Empty for Draft+Candidate (the dentist
  // is still building the plan; Accept/Defer/Decline are patient
  // verbs that fire on Proposed plans).
  const showAcceptVerbs   = isProposed && canAccept && item.status === "Proposed";
  const stateHasActions   = showAcceptVerbs || canSchedule || canUnschedule;

  return (
    <li
      className={`flex flex-col ${declined ? "opacity-60" : ""}`}
      data-testid={`tx-item-${item.id}`}
      data-tutorial-id={item.tutorial_id || undefined}
    >
      <div className={`mx-3 my-2 rounded-lg border border-slate-200 bg-white overflow-hidden ${declined ? "" : ""}`}>
        {/* Zone 1 — What ----------------------------------------------- */}
        <div className="px-3 py-2 bg-slate-50/60 border-b border-slate-200">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className={`text-sm font-semibold text-slate-900 truncate min-w-0 ${declined ? "line-through" : ""}`}>
              {item.display_label || item.procedure_name || "Procedure"}
            </p>
            {item.tooth_number ? (
              <span className="text-[11px] text-slate-500">· Tooth {item.tooth_number}</span>
            ) : null}
            {unitCount > 1 ? (
              <span className="text-[11px] text-slate-500">· {unitCount} units</span>
            ) : null}
            {item.surface ? (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
                {item.surface}
              </span>
            ) : null}
          </div>
        </div>

        {/* Zone 2 — Cost (twin columns: money | time) ------------------
            No header labels — the ₱ symbol and 'min' unit are unambiguous,
            and the divided two-column structure already communicates
            parity. Earlier draft used 'COST TO PATIENT / COST TO
            SCHEDULE' headers; that read as preachy / UX-doc voice
            rather than how a dentist actually talks. */}
        <div className="grid grid-cols-2 divide-x divide-slate-200">
          <div className="px-3 py-2.5">
            <p className="text-base font-bold text-slate-900 tabular-nums">
              {formatPHP(treatmentPlanItemNetCentavos(item))}
              {showLineDiscount ? (
                <span className="ml-2 text-xs font-normal text-slate-400 line-through">
                  {formatPHP(item.fee_centavos)}
                </span>
              ) : null}
            </p>
            {moneyBreakdown ? (
              <p className="text-[11px] text-slate-500 tabular-nums mt-0.5">{moneyBreakdown}</p>
            ) : null}
          </div>
          <div className="px-3 py-2.5">
            <p className="text-base font-bold text-slate-900 tabular-nums">
              {totalChairMin > 0 ? `${totalChairMin} min` : "—"}
            </p>
            {timeBreakdown ? (
              <p className="text-[11px] text-slate-500 tabular-nums mt-0.5">{timeBreakdown}</p>
            ) : null}
            {canEditTime ? (
              <div className="mt-1.5">
                <TimeControl
                  chairMin={singleVisit.chair_min ?? null}
                  onChange={async (mins) => {
                    const { error } = await supabase.rpc("set_procedure_visit_stage", {
                      p_visit_id:  singleVisit.id,
                      p_chair_min: mins ?? 0,
                    });
                    if (error) { toast.error(error.message ?? "Could not update time."); return; }
                    onVisitStagesChanged?.();
                  }}
                  testIdPrefix={`tx-item-${item.id}`}
                />
              </div>
            ) : null}
          </div>
        </div>

        {/* Zone 3 — State (status pill + verbs + phase picker)
            Hidden for execution-mode items (Accepted / Scheduled /
            Completed) — the embedded VisitTimelineStrip below already
            communicates "where is this procedure at" without the
            redundant pill. Cancelled / Declined keep the pill display
            since they're terminal and don't get a timeline. */}
        {!isExecutionMode ? (
        <div className="px-3 py-2 border-t border-slate-200 flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${STATUS_BADGES[item.status] || STATUS_BADGES.Candidate}`}>
            {item.status}
          </span>
          {item.declined_reason ? (
            <span className="text-[11px] text-rose-600 truncate" title={item.declined_reason}>· {item.declined_reason}</span>
          ) : null}
          {stateHasActions ? <span className="text-slate-300">→</span> : null}

          {showAcceptVerbs ? (
            <>
              <button type="button" onClick={onAccept} disabled={busy}
                      data-testid={`tx-item-${item.id}-accept`}
                      title="Patient accepted this item"
                      className="px-2 py-1 text-[11px] font-semibold rounded border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Accept
              </button>
              <button type="button" onClick={onDefer} disabled={busy}
                      data-testid={`tx-item-${item.id}-defer`}
                      title="Patient wants to defer this item"
                      className="px-2 py-1 text-[11px] font-semibold rounded border border-slate-200 text-slate-700 bg-white hover:border-slate-400 disabled:opacity-40 inline-flex items-center gap-1">
                <Pause className="w-3 h-3" /> Defer
              </button>
              <button type="button" onClick={onDecline} disabled={busy}
                      data-testid={`tx-item-${item.id}-decline`}
                      title="Patient declined this item"
                      className="px-2 py-1 text-[11px] font-semibold rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 disabled:opacity-40 inline-flex items-center gap-1">
                <XIcon className="w-3 h-3" /> Decline
              </button>
            </>
          ) : null}
          {canSchedule ? (
            <button type="button" onClick={onSchedule} disabled={busy}
                    data-testid={`tx-item-${item.id}-schedule`}
                    title="Schedule this item onto an appointment"
                    className="px-2 py-1 text-[11px] font-semibold rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-40 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Schedule
            </button>
          ) : null}
          {canUnschedule ? (
            <button type="button" onClick={onUnschedule} disabled={busy}
                    data-testid={`tx-item-${item.id}-unschedule`}
                    title="Remove from appointment (back to Accepted)"
                    className="px-2 py-1 text-[11px] font-semibold rounded border border-slate-200 text-slate-600 bg-white hover:border-slate-400 disabled:opacity-40 inline-flex items-center gap-1">
              <CalendarOff className="w-3 h-3" /> Unschedule
            </button>
          ) : null}

          {showPhasePicker ? (
            <select
              value={phaseValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__new") {
                  const next = phaseNumbers.length === 0 ? 1 : Math.max(...phaseNumbers) + 1;
                  onSetPhase?.(next);
                } else {
                  onSetPhase?.(v === "" ? null : Number(v));
                }
              }}
              disabled={busy}
              data-testid={`tx-item-${item.id}-phase`}
              title="This surface's appointment (tooth has mixed appointments)"
              className="ml-auto text-[11px] font-semibold rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800 hover:border-amber-400 disabled:opacity-40"
            >
              <option value="">Needs appointment</option>
              {phaseNumbers.map((n) => (
                <option key={n} value={n}>
                  {phaseNames?.[String(n)] || `Appointment ${n}`}
                </option>
              ))}
              <option value="__new">+ New appointment</option>
            </select>
          ) : null}
        </div>
        ) : null}

        {/* Zone 3 (execution variant) — embedded visit timeline.
            Shows per-visit progress with inline Book / Mark complete
            actions. Replaces the redundant status pill + verb row for
            Accepted / Scheduled / Completed items. */}
        {isExecutionMode ? (
          <VisitTimelineStrip
            item={item}
            procedure={procedure}
            canEdit={canEdit}
            onScheduleItem={onSchedule}
            onChanged={onVisitStagesChanged}
          />
        ) : null}

        {/* Zone 4 — Tools (low-priority levers) ------------------------ */}
        {(lineEditable || (isDraft && canEdit)) ? (
          <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">
              {lineEditable ? (
                <LineEditControls
                  grossCentavos={item.fee_centavos}
                  currency={item.currency || "PHP"}
                  discountPct={item.discount_pct ?? null}
                  discountAmountCentavos={item.discount_amount_centavos ?? null}
                  discountReason={item.discount_reason ?? null}
                  notes={item.notes ?? null}
                  presets={buildLinePresets({
                    patientSeniorPwdTag: patient?.senior_pwd_tag ?? null,
                  })}
                  onPriceChange={isDraft && onSetPrice    ? async (cents) => onSetPrice(item.id, cents)    : null}
                  onDiscountChange={onSetDiscount         ? async (vals)  => onSetDiscount(item.id, vals)  : null}
                  onNoteChange={isDraft && onSetNote      ? async (n)     => onSetNote(item.id, n)         : null}
                  testIdPrefix={`tx-item-${item.id}`}
                />
              ) : null}
            </div>
            {isDraft && canEdit ? (
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                data-testid={`tx-item-${item.id}-delete`}
                className="shrink-0 p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                title="Remove from plan"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            ) : null}
          </div>
        ) : null}

        {/* Visit plan — multi-visit procedures (Bridge / Crown / RPD /
            Denture / Implant) get an expandable list of stages with
            editable chair time + lab gap + phase assignment. Only
            shown pre-execution (Draft / Proposed); execution mode
            uses the VisitTimelineStrip above instead. */}
        {visits.length >= 2 && !isExecutionMode ? (
          <div className="border-t border-slate-100">
            <VisitPlanBlock
              itemId={item.id}
              visits={item.procedure_visits}
              phaseNumbers={phaseNumbers}
              phaseNames={phaseNames}
              isDraft={isDraft}
              canEdit={canEdit}
              onChanged={onVisitStagesChanged}
            />
          </div>
        ) : null}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------
// VisitPlanBlock — expandable per-item visit-stage editor.
// Defined at module scope so its React element type is stable across
// renders of the parent (the same gotcha called out in ToothChart.jsx).
// ---------------------------------------------------------------------
function VisitPlanBlock({ itemId, visits, phaseNumbers, phaseNames, isDraft, canEdit, onChanged }) {
  const [busy, setBusy] = useState(null);  // visit_id of in-flight op, or 'add'
  const editable = isDraft && canEdit;

  const totalChairMin = useMemo(
    () => (visits || []).reduce((sum, v) => sum + (Number(v.chair_min) || 0), 0),
    [visits]);

  // All visits in one phase = "almost certainly an oversight" signal.
  // Renders an inline nudge inviting the doctor to spread stages across
  // appointments. NOT a hard gate (some clinics legitimately do same-
  // chairside multi-stage work). Toggle to a hard block per-clinic
  // later via clinic_settings.require_visit_phase_assignment_before_propose.
  const phasesUsed = useMemo(() => {
    const set = new Set();
    for (const v of visits || []) {
      if (v.phase_number != null) set.add(Number(v.phase_number));
    }
    return set;
  }, [visits]);
  const allInOnePhase = visits.length >= 2 && phasesUsed.size === 1;

  async function patchStage(visitId, fields) {
    setBusy(visitId);
    try {
      const { error } = await supabase.rpc("set_procedure_visit_stage", {
        p_visit_id:     visitId,
        p_label:        fields.label        ?? null,
        p_chair_min:    fields.chair_min    ?? null,
        p_lab_gap_days: fields.lab_gap_days ?? null,
        p_phase_number: fields.phase_number ?? null,
      });
      if (error) { toast.error(error.message ?? "Could not update visit stage."); return; }
      onChanged?.();
    } finally {
      setBusy(null);
    }
  }

  async function addStage() {
    setBusy("add");
    try {
      const { error } = await supabase.rpc("add_procedure_visit_stage", {
        p_item_id:      itemId,
        p_label:        `Stage ${(visits?.length || 0) + 1}`,
        p_chair_min:    30,
        p_lab_gap_days: null,
        p_phase_number: null,
      });
      if (error) { toast.error(error.message ?? "Could not add stage."); return; }
      onChanged?.();
    } finally {
      setBusy(null);
    }
  }

  async function deleteStage(visitId) {
    if ((visits?.length || 0) <= 1) {
      toast.error("Add another stage before deleting this one.");
      return;
    }
    if (!confirm("Delete this visit stage? The remaining stages will renumber.")) return;
    setBusy(visitId);
    try {
      const { error } = await supabase.rpc("delete_procedure_visit_stage", {
        p_visit_id: visitId,
      });
      if (error) { toast.error(error.message ?? "Could not delete stage."); return; }
      onChanged?.();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="mt-2 rounded-xl border border-slate-200 bg-slate-50/40 px-3 py-3"
      data-testid={`tx-item-${itemId}-visit-plan`}
    >
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="flex items-baseline gap-2">
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
            Visit schedule
          </h4>
          <span className="text-[11px] text-slate-500">
            {visits.length} stage{visits.length === 1 ? "" : "s"}
            {totalChairMin > 0 ? ` · ${totalChairMin} min chair time total` : ""}
          </span>
        </div>
      </div>

      {allInOnePhase ? (
        <div className="mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            All {visits.length} stages are assigned to the same appointment. Most multi-visit
            procedures span across appointments — assign each stage to its own
            appointment below if that's what you intend.
          </span>
        </div>
      ) : null}

      <div className="space-y-2">
        {visits.map((v, idx) => {
          const isLast = idx === visits.length - 1;
          const visitBusy = busy === v.id;
          const frozen = !!v.completed_at;
          const canDeleteStage = editable && !frozen && visits.length > 1;
          const inputDisabled = !editable || visitBusy || frozen;
          const phaseOptionsForVisit = visitPhaseOptions(v, phaseNumbers);

          return (
            <div
              key={v.id}
              className={`px-3 py-2.5 rounded-lg border ${frozen ? "bg-teal-50/40 border-teal-200" : "bg-white border-slate-200"}`}
              data-testid={`tx-item-${itemId}-visit-${v.sequence}`}
            >
              {/* Header row — Visit number + stage label + status pill + delete */}
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-bold text-slate-500 tabular-nums shrink-0 inline-flex items-center justify-center w-16 h-6 rounded-md bg-slate-100">
                  Visit {v.sequence}
                </span>
                <input
                  type="text"
                  defaultValue={v.template_label || ""}
                  disabled={inputDisabled}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val !== (v.template_label || "")) {
                      patchStage(v.id, { label: val || "Untitled stage" });
                    }
                  }}
                  placeholder="Stage name (e.g. Prep & impression)"
                  data-testid={`tx-item-${itemId}-visit-${v.sequence}-label`}
                  className="flex-1 min-w-0 px-2.5 py-1.5 text-sm font-medium rounded border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
                />
                {frozen ? (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-teal-100 text-teal-700 shrink-0">
                    Done
                  </span>
                ) : null}
                {canDeleteStage ? (
                  <button
                    type="button"
                    onClick={() => deleteStage(v.id)}
                    disabled={visitBusy}
                    title="Remove this stage"
                    data-testid={`tx-item-${itemId}-visit-${v.sequence}-delete`}
                    className="p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 shrink-0"
                  >
                    {visitBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                ) : null}
              </div>

              {/* Inputs row — chair time, lab gap, phase assignment */}
              <div className="flex flex-wrap items-end gap-3 pl-16">
                <label className="flex flex-col gap-0.5 shrink-0">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Chair time
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      defaultValue={v.chair_min ?? ""}
                      disabled={inputDisabled}
                      onBlur={(e) => {
                        const val = e.target.value === "" ? null : Number(e.target.value);
                        if (val !== (v.chair_min ?? null)) patchStage(v.id, { chair_min: val ?? 0 });
                      }}
                      data-testid={`tx-item-${itemId}-visit-${v.sequence}-chair-min`}
                      className="w-16 px-2 py-1 text-sm rounded border border-slate-200 tabular-nums text-right disabled:bg-slate-50 disabled:text-slate-500"
                    />
                    <span className="text-xs text-slate-500">min</span>
                  </span>
                </label>

                {!isLast ? (
                  <label className="flex flex-col gap-0.5 shrink-0" title="How many days the lab needs before the next visit">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1">
                      <Hourglass className="w-3 h-3" /> Lab gap
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        defaultValue={v.lab_gap_days ?? ""}
                        disabled={inputDisabled}
                        onBlur={(e) => {
                          const val = e.target.value === "" ? null : Number(e.target.value);
                          if (val !== (v.lab_gap_days ?? null)) patchStage(v.id, { lab_gap_days: val ?? 0 });
                        }}
                        data-testid={`tx-item-${itemId}-visit-${v.sequence}-lab-gap`}
                        className="w-16 px-2 py-1 text-sm rounded border border-slate-200 tabular-nums text-right disabled:bg-slate-50 disabled:text-slate-500"
                      />
                      <span className="text-xs text-slate-500">days</span>
                    </span>
                  </label>
                ) : null}

                <label className="flex flex-col gap-0.5 shrink-0">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Appointment
                  </span>
                  <select
                    value={v.phase_number ?? ""}
                    disabled={inputDisabled}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "__add_stage") {
                        addStage();
                        return;
                      }
                      const val = raw === "" ? null : Number(raw);
                      patchStage(v.id, { phase_number: val });
                    }}
                    data-testid={`tx-item-${itemId}-visit-${v.sequence}-phase`}
                    className="text-sm rounded border border-slate-200 bg-white px-2 py-1 min-w-[140px] disabled:bg-slate-50"
                  >
                    <option value="">Needs appointment</option>
                    {phaseOptionsForVisit.map(({ number, disabled }) => (
                      <option key={number} value={number} disabled={disabled}>
                        {phaseNames?.[String(number)] || `Appointment ${number}`}
                      </option>
                    ))}
                    <option value="__add_stage">+ Add stage</option>
                  </select>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {editable ? (
        <button
          type="button"
          onClick={addStage}
          disabled={busy === "add"}
          data-testid={`tx-item-${itemId}-visit-add`}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-dashed border-slate-300 text-slate-600 hover:border-violet-400 hover:text-violet-700 hover:bg-violet-50/50 disabled:opacity-40"
        >
          {busy === "add" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add stage
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Decline-reason modal.
// ---------------------------------------------------------------------
function DeclineReasonDialog({ reason, onChange, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog" aria-modal="true"
      data-testid="decline-reason-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">Decline this item?</h3>
            <p className="text-sm text-slate-500 mt-1">
              Capture why the patient declined (optional, but recommended for the audit trail).
            </p>
          </div>
        </div>
        <textarea
          value={reason}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Reason (optional)"
          data-testid="decline-reason-input"
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-500"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="decline-reason-cancel"
            className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="decline-reason-confirm"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1.5"
          >
            <XIcon className="w-3.5 h-3.5" /> Confirm decline
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
function groupPlanItems(items, procedures = []) {
  // Multi-tooth items (Bridge / Splint / RPD) get their own group keyed
  // by item.id so each Bridge row shows its full teeth list. Single-
  // tooth items keep grouping by tooth_number so two procedures on the
  // same tooth still appear together. Whole-mouth items also get their
  // own group keyed by item.id because there is no clinical tooth anchor
  // to safely merge around.
  const procedureById = new Map((procedures || []).map((p) => [p.id, p]));
  const map = {};
  const meta = {};
  for (const it of items) {
    const procedure = procedureById.get(it.procedure_id) || null;
    const teethArr = Array.isArray(it.teeth)
      ? it.teeth.map((t) => (typeof t === "object" ? t?.tooth_number : t)).filter(Boolean)
      : [];
    const isMulti = teethArr.length > 1;
    const isWholeMouth = !it.tooth_number && isWholeMouthProcedureForPlanItem(it, procedure);
    const kind = isMulti
      ? "multi_tooth"
      : it.tooth_number
        ? "tooth"
        : isWholeMouth
          ? "whole_mouth"
          : "missing_tooth";
    const key = isMulti
      ? `multi:${it.id}`
      : it.tooth_number
        ? `tooth:${it.tooth_number}`
        : `${kind}:${it.id}`;
    (map[key] = map[key] || []).push(it);
    if (!meta[key]) {
      const firstTooth = isMulti && teethArr.length ? Number(teethArr[0]) : null;
      meta[key] = {
        key,
        kind,
        label: buildPlanGroupLabel(kind, it.tooth_number, teethArr),
        tooth: kind === "tooth" ? (it.tooth_number || null) : null,
        teeth: isMulti ? teethArr : null,
        sortKey: isMulti && teethArr.length
          ? firstTooth
          : (it.tooth_number ? Number(it.tooth_number) : Number.POSITIVE_INFINITY),
      };
    }
  }
  return Object.keys(map)
    .sort((a, b) => (meta[a]?.sortKey ?? 0) - (meta[b]?.sortKey ?? 0))
    .map((key) => ({
      key: meta[key].key,
      kind: meta[key].kind,
      label: meta[key].label,
      tooth: meta[key].tooth,
      teeth: meta[key].teeth,
      items: map[key].sort((x, y) => (x.sort_order ?? 0) - (y.sort_order ?? 0)),
      subtotal: map[key]
        .filter((i) => i.status !== "Declined")
        .reduce((sum, i) => sum + (i.fee_centavos ?? 0), 0),
    }));
}

function isWholeMouthProcedureForPlanItem(item, procedure) {
  if (procedure) {
    return procedure.tooth_specific === false || procedure.tooth_count === "none";
  }
  return item.tooth_count === "none" || item.tooth_specific === false;
}

function buildPlanGroupLabel(kind, tooth, teeth = []) {
  if (kind === "tooth") return `Tooth ${tooth}`;
  if (kind === "multi_tooth") return `${teeth.length}-unit · ${teeth.map((t) => `#${t}`).join(", ")}`;
  if (kind === "whole_mouth") return "Whole-mouth";
  return "Missing tooth";
}

function computeTotals(items) {
  // openTotal = the NET total after per-line discounts (what the
  // patient will actually be billed). declinedTotal stays at gross for
  // the "you saved this much by declining" reference.
  let openTotal = 0;
  let declinedTotal = 0;
  for (const it of items) {
    if (it.status === "Declined") {
      declinedTotal += it.fee_centavos ?? 0;
    } else {
      openTotal += treatmentPlanItemNetCentavos(it);
    }
  }
  return { openTotal, declinedTotal };
}
